import React, { useState, useEffect, useRef } from 'react';
import { SoundManager, HapticManager } from './sounds';
import { builtInDictionaries, topicCategories, getRandomPairs, getMaxPairsInDictionary, getTopicsByMode } from './builtInDictionaries';
import { useAuth } from './contexts/AuthContext';
import { apiService } from './services/api';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import EmailVerificationForm from './components/EmailVerificationForm';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import UserDashboard from './components/UserDashboard';
import SuspendedUserModal from './components/SuspendedUserModal';
import AIDictionaryCreator from './components/AIDictionaryCreator';
import CompetitiveLobby from './components/CompetitiveLobby';
import CompetitiveGame from './components/CompetitiveGame';
import GameTypeSelection from './components/GameTypeSelection';
import TopicSelection from './components/TopicSelection';
import PairCountSelection from './components/PairCountSelection';
import MultipleChoiceSetup from './components/MultipleChoiceSetup';
import MultipleChoiceGame from './components/MultipleChoiceGame';
import { MultipleChoiceSetupState } from './utils/multipleChoiceLogic';
import socketService from './services/socket';
import seedrandom from 'seedrandom';
import {
  MatchingMode,
  detectMatchingMode,
  checkMatch as checkMatchLogic,
  getFilledItems,
  getGameItemsForMode
} from './utils/matchingLogic';
import './App.css';

interface MovingRectangle {
  id: string;
  content: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  scale: number;
  collisionEffect: boolean;
  width?: number;
  height?: number;
  fontSize?: string;
  lines?: string[];
  isLongContent?: boolean;
}

interface DropZone {
  id: 'upper' | 'lower' | 'item1' | 'item2' | 'category' | string;
  content: string | null;
  zoneType?: 'default' | 'item' | 'category' | 'label';
  labelName?: string; // For sort mode label boxes
}

interface Particle {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

// Dynamic API URL based on current domain
const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  const hostname = window.location.hostname;
  
  // For universalmatching.com domains, use the same domain
  if (hostname === 'universalmatching.com' || hostname === 'www.universalmatching.com') {
    return `https://${hostname}`;
  }
  
  // For Vercel deployments, use relative API calls (proxy to backend)
  if (hostname.includes('vercel.app')) {
    return ''; // Use relative URLs for Vercel
  }
  
  // Default for local development
  return 'http://localhost:5000';
};

const API_BASE_URL = getApiBaseUrl();

const App: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [rectangles, setRectangles] = useState<MovingRectangle[]>([]);
  const [dropZones, setDropZones] = useState<DropZone[]>([
    { id: 'upper', content: null, zoneType: 'default' },
    { id: 'lower', content: null, zoneType: 'default' }
  ]);
  const [labelZones, setLabelZones] = useState<DropZone[]>([]); // For sort mode
  const [isVerticalLayout, setIsVerticalLayout] = useState(true); // For sort mode layout
  const [currentMatchingMode, setCurrentMatchingMode] = useState<MatchingMode>('one-to-one');
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [message, setMessage] = useState('Choose a game mode to start playing');
  const [, setGameItems] = useState<string[]>([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [streak, setStreak] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [soundEnabled] = useState(false);
  const [hapticEnabled] = useState(false);
  const [successPopup, setSuccessPopup] = useState<string | null>(null);
  const [mismatchPopup, setMismatchPopup] = useState<string | null>(null);
  const [gameStartTime, setGameStartTime] = useState<number | null>(null);
  const [bestTime, setBestTime] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [selectedPairCount, setSelectedPairCount] = useState<number>(6);
  const [maxPairs, setMaxPairs] = useState<number>(12);
  const [gameQueue, setGameQueue] = useState<string[]>([]);
  const [completedPairs, setCompletedPairs] = useState<number>(0);
  const [matchedItems, setMatchedItems] = useState<Set<string>>(new Set());
  const [showGameTypeSelection, setShowGameTypeSelection] = useState<boolean>(false);
  const [selectedGameType, setSelectedGameType] = useState<MatchingMode | null>(null);
  const [showBuiltInGames, setShowBuiltInGames] = useState<boolean>(false);
  const [showAIDictionaryCreator, setShowAIDictionaryCreator] = useState<boolean>(false);
  const [currentGameData, setCurrentGameData] = useState<any | null>(null); // Supports both one-to-one and category dictionaries
  const [isBuiltInGame, setIsBuiltInGame] = useState<boolean>(false);
  const [isAIGame, setIsAIGame] = useState<boolean>(false);
  const [isSavedGame, setIsSavedGame] = useState<boolean>(false);
  const [gameMode, setGameMode] = useState<'freeplay' | 'competition'>('freeplay');
  const [currentAssignmentId, setCurrentAssignmentId] = useState<number | null>(null);
  const [currentGameId, setCurrentGameId] = useState<number | null>(null);
  const [requiredPairCount, setRequiredPairCount] = useState<number | null>(null);
  const [sessionStats, setSessionStats] = useState({ totalPairs: 0, totalAttempts: 0, correctMatches: 0 });
  const [showCompetitionPopup, setShowCompetitionPopup] = useState(false);
  const [showHelpPopup, setShowHelpPopup] = useState(false);
  const [showGameInstructions, setShowGameInstructions] = useState(false);
  const gameInstructionsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [showSinglePlayerResults, setShowSinglePlayerResults] = useState(false);
  const [singlePlayerCompletionTime, setSinglePlayerCompletionTime] = useState<number | null>(null);
  const [darkMode, setDarkMode] = useState(false);
  const [uploadedDictionary, setUploadedDictionary] = useState<{items: string[], gameDataMapping: Record<string, string> | any, message: string} | null>(null);

  // Competitive mode states
  const [showCompetitiveLobby, setShowCompetitiveLobby] = useState(false);
  const [showTopicSelection, setShowTopicSelection] = useState(false);
  const [showPairCountSelection, setShowPairCountSelection] = useState(false);
  const [competitiveRoom, setCompetitiveRoom] = useState<any>(null);
  const [competitiveParticipants, setCompetitiveParticipants] = useState<any[]>([]);
  const [isCompetitiveHost, setIsCompetitiveHost] = useState(false);
  const [competitiveGameActive, setCompetitiveGameActive] = useState(false);
  const [competitiveResultsShown, setCompetitiveResultsShown] = useState(false);
  const [competitiveStats, setCompetitiveStats] = useState({
    totalAttempts: 0,
    correctMatches: 0,
    wrongMatches: 0,
    startTime: 0
  });
  const [gameSpeed, setGameSpeed] = useState<number>(5); // Speed level 1-10, default 5 (middle)
  const [speedHistory, setSpeedHistory] = useState<{speed: number, timestamp: number}[]>([]); // Track speed changes
  const [competitiveOpponents, setCompetitiveOpponents] = useState<Map<number, any>>(new Map()); // Track opponents progress
  const [currentUserProgress, setCurrentUserProgress] = useState<any>(null); // Track current user's own progress
  
  // Multiple Choice game state
  const [showMultipleChoiceSetup, setShowMultipleChoiceSetup] = useState(false);
  const [showMultipleChoiceGame, setShowMultipleChoiceGame] = useState(false);
  const [multipleChoiceSetup, setMultipleChoiceSetup] = useState<MultipleChoiceSetupState | null>(null);

  // Authentication UI state
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [showUserDashboard, setShowUserDashboard] = useState(false);
  const [dashboardInitialRole, setDashboardInitialRole] = useState<'teacher' | 'student' | null>(null);
  const [userRoles, setUserRoles] = useState<{isTeacher: boolean; isStudent: boolean; hasBothRoles: boolean} | null>(null);
  const [saveDictionaryName, setSaveDictionaryName] = useState('');
  const [showSaveDictionary, setShowSaveDictionary] = useState(false);
  const [showLogoutSuccess, setShowLogoutSuccess] = useState(false);
  const [showLoginSuccess, setShowLoginSuccess] = useState(false);
  const [showLoginFailure, setShowLoginFailure] = useState(false);
  const [showRegisterSuccess, setShowRegisterSuccess] = useState(false);
  const [showRegisterFailure, setShowRegisterFailure] = useState(false);
  const [loginFailureMessage, setLoginFailureMessage] = useState('');
  const [registerFailureMessage, setRegisterFailureMessage] = useState('');
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [pendingUser, setPendingUser] = useState<{userId: number, email: string} | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Notification states
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  // Suspension states
  const [suspensionReason, setSuspensionReason] = useState('');
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);

  // Check system preference on mount
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setDarkMode(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setDarkMode(e.matches);
    mediaQuery.addEventListener('change', handler);
    
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Apply theme to body element to ensure full page background
  useEffect(() => {
    document.body.className = darkMode ? 'dark-mode' : 'light-mode';
    document.documentElement.className = darkMode ? 'dark-mode' : 'light-mode';
  }, [darkMode]);

  // Determine layout orientation for sort mode (vertical vs horizontal)
  useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Vertical if wide screen (aspect ratio > 1.2)
      // Horizontal if narrow/mobile (aspect ratio <= 1.2)
      setIsVerticalLayout(width / height > 1.2);
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  // Load user roles when user logs in
  useEffect(() => {
    const loadRoles = async () => {
      if (user) {
        try {
          const roles = await apiService.getUserRoles();
          setUserRoles(roles);
        } catch (error) {
          console.error('Failed to load user roles:', error);
          setUserRoles(null);
        }
      } else {
        setUserRoles(null);
      }
    };
    loadRoles();
  }, [user]);

  // Disable body scroll when modals are open
  useEffect(() => {
    if (showUserDashboard || showAuth) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showUserDashboard, showAuth]);

  // Load unread notification count when user is authenticated
  useEffect(() => {
    const loadUnreadCount = async () => {
      if (isAuthenticated) {
        try {
          const response = await apiService.getUnreadNotificationCount();
          setUnreadNotificationCount(response.unreadCount);
        } catch (error) {
          console.error('Failed to load unread notification count:', error);
        }
      } else {
        setUnreadNotificationCount(0);
      }
    };

    loadUnreadCount();
  }, [isAuthenticated]);

  // Check suspension status when user is authenticated
  useEffect(() => {
    const checkSuspension = async () => {
      if (isAuthenticated) {
        try {
          const response = await apiService.checkSuspensionStatus();
          if (response.isSuspended) {
            setSuspensionReason(response.suspensionData?.reason || 'Account suspended');
            setShowSuspensionModal(true);
            // Hide all other modals when suspended
            setShowAuth(false);
            setShowUserDashboard(false);
          } else {
            setSuspensionReason('');
            setShowSuspensionModal(false);
          }
        } catch (error) {
          console.error('Failed to check suspension status:', error);
        }
      } else {
        setSuspensionReason('');
        setShowSuspensionModal(false);
      }
    };

    checkSuspension();
  }, [isAuthenticated]);

  // Function to refresh notification count
  const refreshNotificationCount = async () => {
    if (isAuthenticated) {
      try {
        const response = await apiService.getUnreadNotificationCount();
        setUnreadNotificationCount(response.unreadCount);
      } catch (error) {
        console.error('Failed to refresh notification count:', error);
      }
    }
  };

  const [currentGameAreaDimensions, setCurrentGameAreaDimensions] = useState({ width: 600, height: 450 });
  const [currentRectangleDimensions, setCurrentRectangleDimensions] = useState({ width: 90, height: 50, fontSize: '0.9rem' });
  const [draggedRectangle, setDraggedRectangle] = useState<{ id: string; content: string; x: number; y: number; rotation: number; scale: number; width?: number; height?: number; fontSize?: string; lines?: string[]; isLongContent?: boolean } | null>(null);
  const [savedRectangleStates, setSavedRectangleStates] = useState<MovingRectangle[]>([]);
  const gameAreaRef = useRef<HTMLDivElement>(null);
  const soundManager = useRef(new SoundManager());
  const hapticManager = useRef(new HapticManager());

  // Show instructions for complex game modes
  useEffect(() => {
    if (gameStarted && (currentMatchingMode === 'category' || currentMatchingMode === 'sort')) {
      setShowGameInstructions(true);
      if (gameInstructionsTimerRef.current) clearTimeout(gameInstructionsTimerRef.current);
      gameInstructionsTimerRef.current = setTimeout(() => {
        setShowGameInstructions(false);
      }, 12000);
    } else {
      setShowGameInstructions(false);
    }
    return () => {
      if (gameInstructionsTimerRef.current) clearTimeout(gameInstructionsTimerRef.current);
    };
  }, [gameStarted, currentMatchingMode]);

  // Safeguard: if game is running but no rectangles left and no items in drop zones, end the game
  useEffect(() => {
    if (!gameStarted || completedPairs === 0) return;
    const dropZoneItems = dropZones.filter(z => z.content).length;
    const labelZoneItems = labelZones.filter(z => z.content).length;
    if (rectangles.length === 0 && dropZoneItems === 0 && labelZoneItems === 0) {
      const timer = setTimeout(() => {
        handleGameCompletion();
      }, 500);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStarted, rectangles.length, dropZones, labelZones, completedPairs]);

  // Listen for opponent progress updates in competitive mode
  React.useEffect(() => {
    if (!competitiveGameActive || !competitiveRoom || !user) return;

    const handleOpponentProgress = (data: { userId: number; username?: string; correct: boolean }) => {
      if (data.userId === user.id) return;

      setCompetitiveOpponents(prev => {
        const updated = new Map(prev);
        const opponent = updated.get(data.userId) || {
          userId: data.userId,
          username: data.username || 'Player',
          completed: 0,
          total: competitiveRoom.pairCount,
          attempts: 0,
          accuracy: 100,
          finished: false
        };

        if (data.username && opponent.username === 'Player') {
          opponent.username = data.username;
        }

        opponent.attempts += 1;
        if (data.correct) {
          opponent.completed += 1;
        }
        opponent.accuracy = opponent.attempts > 0 ? (opponent.completed / opponent.attempts) * 100 : 100;

        updated.set(data.userId, opponent);
        return updated;
      });
    };

    const handlePlayerCompleted = (data: { userId: number; username: string; completionTime: number; accuracy: number; score: number }) => {
      // If it's the current user, update currentUserProgress instead
      if (data.userId === user.id) {
        setCurrentUserProgress((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            completed: competitiveRoom.pairCount,
            finished: true,
            completionTime: data.completionTime,
            accuracy: data.accuracy,
            score: data.score
          };
        });
        return;
      }

      // Update opponent
      setCompetitiveOpponents(prev => {
        const updated = new Map(prev);
        const existingOpponent = updated.get(data.userId);
        updated.set(data.userId, {
          userId: data.userId,
          username: data.username,
          completed: competitiveRoom.pairCount,
          total: competitiveRoom.pairCount,
          attempts: existingOpponent?.attempts || competitiveRoom.pairCount,
          accuracy: data.accuracy,
          finished: true,
          completionTime: data.completionTime,
          score: data.score
        });
        return updated;
      });
    };

    socketService.on('player_progress_update', handleOpponentProgress);
    socketService.on('player_completed', handlePlayerCompleted);

    return () => {
      socketService.off('player_progress_update', handleOpponentProgress);
      socketService.off('player_completed', handlePlayerCompleted);
    };
  }, [competitiveGameActive, competitiveRoom, user]);

  const createParticles = (x: number, y: number, count: number = 20) => {
    // Keep particles simple
    const rectangleCount = rectangles.length;
    const adjustedCount = rectangleCount > 12 ? 5 : count; // Much fewer particles for large games
    
    const newParticles: Particle[] = [];
    for (let i = 0; i < adjustedCount; i++) {
      newParticles.push({
        id: `particle-${Date.now()}-${i}`,
        x: x + (Math.random() - 0.5) * 50,
        y: y + (Math.random() - 0.5) * 50,
        vx: (Math.random() - 0.5) * 10,
        vy: (Math.random() - 0.5) * 10 - 5,
        life: 1,
        maxLife: 30 + Math.random() * 20
      });
    }
    setParticles(prev => [...prev, ...newParticles]);
  };


  const isEmoji = (text: string): boolean => {
    // Enhanced emoji detection including flag emojis, animals, and general emojis
    const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]/u;
    
    // Special check for flag emojis (they use two regional indicator symbols)
    const flagRegex = /[\u{1F1E0}-\u{1F1FF}][\u{1F1E0}-\u{1F1FF}]/u;
    
    return emojiRegex.test(text) || flagRegex.test(text);
  };

  const getEmojiClass = (text: string): string => {
    return isEmoji(text) ? 'emoji' : '';
  };

  const getGameAreaDimensions = (rectangleCount: number) => {
    // Get actual game area dimensions from the DOM element
    if (gameAreaRef.current) {
      const rect = gameAreaRef.current.getBoundingClientRect();
      return { width: rect.width, height: rect.height };
    }
    
    // Fallback to responsive calculations if DOM element not available
    const screenWidth = window.innerWidth;
    if (screenWidth <= 480) {
      // Very small mobile screens
      return { width: Math.min(screenWidth * 0.95, 400), height: 250 };
    } else if (screenWidth <= 768) {
      // Mobile/tablet screens
      return { width: Math.min(screenWidth * 0.9, 600), height: 300 };
    } else {
      // Desktop screens - use original logic
      if (rectangleCount <= 6) {
        return { width: 600, height: 450 };
      } else if (rectangleCount <= 12) {
        return { width: 800, height: 500 };
      } else if (rectangleCount <= 18) {
        return { width: 1000, height: 600 };
      } else {
        return { width: 1200, height: 700 };
      }
    }
  };

  const getRectangleDimensions = (rectangleCount: number) => {
    // Get screen size for responsive rectangle sizing
    const screenWidth = window.innerWidth;
    const isMobile = screenWidth <= 768;
    
    // Base dimensions - smaller on mobile
    const baseScale = isMobile ? 0.7 : 1.0;
    
    // Scale rectangle size down as count increases
    if (rectangleCount <= 6) {
      return { 
        width: Math.floor(90 * baseScale), 
        height: Math.floor(50 * baseScale), 
        fontSize: isMobile ? '0.75rem' : '0.9rem' 
      };
    } else if (rectangleCount <= 12) {
      return { 
        width: Math.floor(80 * baseScale), 
        height: Math.floor(45 * baseScale), 
        fontSize: isMobile ? '0.7rem' : '0.8rem' 
      };
    } else if (rectangleCount <= 18) {
      return { 
        width: Math.floor(70 * baseScale), 
        height: Math.floor(40 * baseScale), 
        fontSize: isMobile ? '0.65rem' : '0.75rem' 
      };
    } else {
      return { 
        width: Math.floor(65 * baseScale), 
        height: Math.floor(38 * baseScale), 
        fontSize: isMobile ? '0.6rem' : '0.7rem' 
      };
    }
  };

  // Helper function to measure text width in pixels - FIXED to match actual rendering
  const measureTextWidth = (text: string, fontSize: string, fontFamily: string = 'Inter, sans-serif'): number => {
    if (typeof window === 'undefined') return text.length * 8; // Fallback estimate
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return text.length * 8; // Fallback estimate
    
    // CRITICAL: Match the actual CSS font properties used in rectangles
    context.font = `600 ${fontSize} ${fontFamily}`; // Include font-weight: 600
    const width = context.measureText(text).width;
    console.log('MEASURING:', text, 'width:', width, 'fontSize:', fontSize);
    return width;
  };

  // FIXED text wrapping algorithm with proper line breaking
  const wrapText = (text: string, maxWidth: number, fontSize: string): string[] => {
    console.log('wrapText called with:', text, 'maxWidth:', maxWidth, 'fontSize:', fontSize);
    
    // ALLOW LONGER TEXT - only truncate extremely long text
    const MAX_CHARS = 250; 
    let processedText = text;
    if (text.length > MAX_CHARS) {
      const truncated = text.substring(0, MAX_CHARS);
      const lastSpace = truncated.lastIndexOf(' ');
      processedText = lastSpace > MAX_CHARS - 30 ? 
        truncated.substring(0, lastSpace) + '...' : 
        truncated + '...';
    }
    
    const words = processedText.split(' ').filter(word => word.length > 0);
    console.log('Words to wrap:', words);
    
    if (words.length === 0) return [processedText];
    if (words.length === 1) return [words[0]];
    
    // BALANCED LINE DISTRIBUTION ALGORITHM
    const targetLinesCount = Math.min(4, Math.ceil(words.length / 6)); // Aim for balanced distribution
    const wordsPerLine = Math.ceil(words.length / targetLinesCount);
    
    console.log('Target lines:', targetLinesCount, 'Words per line:', wordsPerLine);
    
    const lines: string[] = [];
    let wordIndex = 0;
    
    for (let lineNum = 0; lineNum < targetLinesCount && wordIndex < words.length; lineNum++) {
      let currentLine = '';
      let wordsInThisLine = 0;
      
      // Add words to current line, aiming for balance
      while (wordIndex < words.length && wordsInThisLine < wordsPerLine) {
        const word = words[wordIndex];
        const testLine = currentLine ? currentLine + ' ' + word : word;
        const testWidth = measureTextWidth(testLine, fontSize);
        
        console.log('Line', lineNum, 'testing:', testLine, 'width:', testWidth, 'maxWidth:', maxWidth, 'fontSize:', fontSize);
        
        if (testWidth <= maxWidth || wordsInThisLine === 0) {
          // Add word if it fits or it's the first word
          currentLine = testLine;
          wordIndex++;
          wordsInThisLine++;
        } else {
          // Word doesn't fit, break to next line
          break;
        }
      }
      
      if (currentLine) {
        lines.push(currentLine);
      }
    }
    
    // If there are remaining words, add them to the last line
    if (wordIndex < words.length) {
      const remainingWords = words.slice(wordIndex).join(' ');
      if (lines.length > 0) {
        lines[lines.length - 1] += ' ' + remainingWords;
      } else {
        lines.push(remainingWords);
      }
    }
    
    console.log('Final lines:', lines);
    return lines.filter(line => line.trim().length > 0);
  };

  // Calculate dynamic rectangle dimensions based on content
  const getDynamicRectangleDimensions = (content: string, rectangleCount: number) => {
    const isMobile = window.innerWidth <= 768;
    
    // Get base dimensions from original function
    const baseDims = getRectangleDimensions(rectangleCount);
    
    // For emojis or single characters, use standard size
    if (isEmoji(content) || content.length <= 3) {
      return {
        ...baseDims,
        lines: [content]
      };
    }
    
    // Determine if this is long content that needs extensive padding
    const isLongContent = content.length > 20; // Lowered threshold: More than 20 characters = long content
    
    // Calculate dynamic dimensions for longer text
    const baseFontSize = baseDims.fontSize;
    const baseWidth = baseDims.width;
    const baseHeight = baseDims.height;
    
    // Adaptive width and padding based on content length - more generous for all content
    let minWidth, maxWidth, paddingSize;
    if (isLongContent) {
      // Long content: use extensive padding and wider rectangles
      minWidth = Math.max(250, baseWidth * 1.5); // More generous minimum width
      maxWidth = 700; // Increased maximum width
      paddingSize = 100; // 50px per side CSS padding
    } else {
      // Short content: use minimal padding and standard rectangles
      minWidth = Math.max(150, baseWidth * 1.2); // More generous for short content too
      maxWidth = 400; // Increased maximum width for short content
      paddingSize = 40; // 20px per side CSS padding
    }
    
    // Find optimal width by trying different widths and picking the best balance
    let bestWidth = minWidth;
    let bestLines: string[] = [];
    let bestScore = Infinity;
    
    for (let width = minWidth; width <= maxWidth; width += 10) {
      const lines = wrapText(content, width - paddingSize, baseFontSize); // Adaptive padding
      
      // Score based on: line count penalty + MINIMAL width penalty + line length variance
      const lineCountPenalty = Math.pow(lines.length, 2.5); // Strong preference for fewer lines
      const widthPenalty = (width - minWidth) / 100; // MUCH LESS penalty for wider rectangles
      const lineLengths = lines.map(line => line.length);
      const avgLength = lineLengths.reduce((sum, len) => sum + len, 0) / lines.length;
      const variance = lineLengths.reduce((sum, len) => sum + Math.pow(len - avgLength, 2), 0) / lines.length;
      
      const score = lineCountPenalty + widthPenalty + variance / 10;
      
      if (score < bestScore) {
        bestScore = score;
        bestWidth = width;
        bestLines = lines;
      }
    }
    
    const optimalWidth = bestWidth;
    const optimalLines = bestLines;
    
    console.log('FINAL WIDTH CALCULATION for:', content.substring(0, 30) + '...', 'optimalWidth:', optimalWidth, 'lines:', optimalLines.length, 'lines:', optimalLines);
    
    // Calculate height based on number of lines with generous spacing
    const lineHeight = 1.4; // More generous line spacing
    const fontSizeNum = parseFloat(baseFontSize.replace('rem', '')) * 16; // Convert rem to px
    const textHeight = optimalLines.length * fontSizeNum * lineHeight;
    
    // Balanced padding to avoid rectangles being too thin or fat
    let totalPadding;
    switch (optimalLines.length) {
      case 1:
        totalPadding = 16; // Standard single line padding
        break;
      case 2:
        totalPadding = 22; // Two lines 
        break;
      case 3:
        totalPadding = 30; // Three lines
        break;
      case 4:
        totalPadding = 40; // Four lines - balanced padding
        break;
      default:
        totalPadding = 40; // Fallback for edge cases
        break;
    }
    
    const optimalHeight = Math.max(baseHeight, Math.ceil(textHeight + totalPadding));
    
    // Adjust font size for mobile to fit fixed-width rectangles
    let adjustedFontSize = baseFontSize;
    if (isMobile) {
      // Reduce font size on mobile for ALL text to prevent overflow
      const baseFontNum = parseFloat(baseFontSize.replace('rem', ''));
      const mobileFontSize = Math.max(0.5, baseFontNum * 0.7); // Even smaller for mobile, minimum 0.5rem
      adjustedFontSize = mobileFontSize + 'rem';
    }
    
    return {
      width: Math.floor(optimalWidth),
      height: Math.floor(optimalHeight),
      fontSize: adjustedFontSize,
      lines: optimalLines,
      isLongContent: isLongContent // Pass this info for CSS padding (>20 chars)
    };
  };

  // Validate and convert JSON to bidirectional format
  const validateAndConvertDictionary = (data: any): { items: string[], message: string } => {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid JSON format');
    }

    const originalEntries = Object.entries(data);
    if (originalEntries.length === 0) {
      throw new Error('Game file is empty');
    }

    // Detect matching mode from metadata
    const matchingMode = detectMatchingMode(data);

    // Handle sort mode: convert metadata.categories to labels/items format
    if (matchingMode === 'sort') {
      const categories = data.metadata?.categories;
      if (!categories || typeof categories !== 'object') {
        throw new Error('Sort mode requires metadata.categories');
      }
      const labels = Object.keys(categories);
      const items: Record<string, string> = {};
      let totalItems = 0;
      for (const [label, categoryItems] of Object.entries(categories)) {
        if (Array.isArray(categoryItems)) {
          for (const item of categoryItems) {
            items[item as string] = label;
            totalItems++;
          }
        }
      }
      if (totalItems === 0) {
        throw new Error('Sort mode categories are empty');
      }
      // Return dummy items array (not used for sort), message with count
      return {
        items: ['__sort_mode__'],
        message: `Sort & Classify game loaded ✓ | ${totalItems} items in ${labels.length} categories`
      };
    }

    // Handle category mode: validate categories exist
    if (matchingMode === 'category') {
      const categories = data.metadata?.categories;
      if (!categories || typeof categories !== 'object') {
        throw new Error('Category mode requires metadata.categories');
      }
      // For category mode, we still need the bidirectional pairs
      // Filter out metadata key and process the rest
      const pairEntries = originalEntries.filter(([key]) => key !== 'metadata');
      if (pairEntries.length === 0) {
        throw new Error('Category mode requires word pairs in addition to metadata');
      }
    }

    // For one-to-one and category modes: process flat key-value pairs
    // Filter out metadata key
    const flatEntries = originalEntries.filter(([key]) => key !== 'metadata');

    // Check character length restrictions
    const MAX_CHAR_LENGTH = 200;
    for (const [key, value] of flatEntries) {
      if (typeof key === 'string' && key.length > MAX_CHAR_LENGTH) {
        throw new Error(`Key "${key.substring(0, 50)}..." exceeds maximum length of ${MAX_CHAR_LENGTH} characters`);
      }
      if (typeof value === 'string' && (value as string).length > MAX_CHAR_LENGTH) {
        throw new Error(`Value "${(value as string).substring(0, 50)}..." exceeds maximum length of ${MAX_CHAR_LENGTH} characters`);
      }
    }

    // Build data without metadata for bidirectional check
    const dataWithoutMetadata: Record<string, string> = {};
    for (const [key, value] of flatEntries) {
      if (typeof value === 'string') {
        dataWithoutMetadata[key] = value;
      }
    }

    // Check if already bidirectional
    const isBidirectional = Object.entries(dataWithoutMetadata).every(([key, value]) =>
      dataWithoutMetadata[value] === key
    );

    let processedData: Record<string, string>;
    let conversionMessage = '';

    if (isBidirectional) {
      processedData = dataWithoutMetadata;
      conversionMessage = 'Game file is already in the correct format ✓';
    } else {
      processedData = { ...dataWithoutMetadata };
      let addedPairs = 0;

      for (const [key, value] of Object.entries(dataWithoutMetadata)) {
        if (!processedData[value]) {
          processedData[value] = key;
          addedPairs++;
        }
      }

      conversionMessage = `Auto-converted to bidirectional: added ${addedPairs} reverse mappings ✓`;
    }

    // Convert to items array format
    const items = Object.entries(processedData).flat();

    const modeLabel = matchingMode === 'category' ? 'Pair & Classify' : 'Pair Match';
    return {
      items,
      message: `${modeLabel} game loaded ✓ | ${conversionMessage} | ${getMaxPairsInDictionary(processedData)} pairs loaded`
    };
  };

  const uploadDictionary = async (file: File) => {
    setIsLoading(true);

    try {
      // First, validate and process the file client-side
      const fileText = await file.text();
      let jsonData;

      try {
        jsonData = JSON.parse(fileText);
      } catch (parseError) {
        throw new Error('Invalid JSON file format');
      }

      // Detect matching mode early
      const matchingMode = detectMatchingMode(jsonData);

      // Validate the dictionary
      const { items, message } = validateAndConvertDictionary(jsonData);

      // Process completely client-side - no server call needed
      console.log('Processing dictionary client-side:', { matchingMode, itemCount: items.length, message });

      let gameDataMapping: any;

      if (matchingMode === 'sort') {
        // Sort mode: convert metadata.categories to labels/items format
        const categories = jsonData.metadata.categories;
        const labels = Object.keys(categories);
        const sortItems: Record<string, string> = {};
        for (const [label, categoryItems] of Object.entries(categories)) {
          if (Array.isArray(categoryItems)) {
            for (const item of categoryItems) {
              sortItems[item as string] = label;
            }
          }
        }
        gameDataMapping = {
          labels,
          items: sortItems,
          metadata: { matchingMode: 'sort' as const }
        };
      } else if (matchingMode === 'category') {
        // Category mode: preserve metadata, rebuild pairs without metadata key
        const pairsOnly: Record<string, string> = {};
        for (const [key, value] of Object.entries(jsonData)) {
          if (key !== 'metadata' && typeof value === 'string') {
            pairsOnly[key] = value;
            // Add reverse if missing
            if (!jsonData[value as string]) {
              pairsOnly[value as string] = key;
            }
          }
        }
        gameDataMapping = {
          ...pairsOnly,
          metadata: jsonData.metadata
        };
      } else {
        // One-to-one mode: create game data mapping from items
        if (items.length === 0) {
          throw new Error('No items processed from dictionary');
        }
        gameDataMapping = Object.fromEntries(
          Array.from({ length: items.length / 2 }, (_, i) => [
            [items[i * 2], items[i * 2 + 1]],
            [items[i * 2 + 1], items[i * 2]]
          ]).flat()
        );
      }

      // Store uploaded dictionary data for configuration
      setUploadedDictionary({ items, gameDataMapping, message });

      // Set max pairs and default selection for uploaded dictionary using proper pair counting
      const maxPairsForUploaded = getMaxPairsInDictionary(gameDataMapping);
      setMaxPairs(maxPairsForUploaded);
      setSelectedPairCount(Math.min(selectedPairCount, maxPairsForUploaded));

      setMessage(`📁 ${message} - Choose number of matches and start game`);
    } catch (error) {
      console.error('Dictionary upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setMessage(`❌ ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle AI-generated dictionary
  const handleAIDictionary = (dictionary: Record<string, string>) => {
    try {
      const { items, message } = validateAndConvertDictionary(dictionary);

      setUploadedDictionary({
        gameDataMapping: dictionary,
        items: items,
        message: `🤖 AI Generated Game: ${message}`
      });
      setCurrentGameData(dictionary);

      // Set max pairs for AI dictionary using proper pair counting
      const maxPairsForAI = getMaxPairsInDictionary(dictionary);
      setMaxPairs(maxPairsForAI);
      setSelectedPairCount(Math.min(selectedPairCount, maxPairsForAI));

      setMessage('🤖 AI game loaded! Select number of matches to play.');
      setShowAIDictionaryCreator(false);
      setIsAIGame(true);
      setIsBuiltInGame(false);
    } catch (error) {
      console.error('AI dictionary processing error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setMessage(`❌ Failed to process AI game: ${errorMessage}`);
    }
  };

  // Handle assignment game start - directly start game without config screen
  const handleAssignmentGame = (dictionary: any, requiredPairs?: number) => {
    console.log('📚 handleAssignmentGame called:', {
      requiredPairs,
      requiredPairsType: typeof requiredPairs,
      requiredPairsIsUndefined: requiredPairs === undefined,
      requiredPairsIsNull: requiredPairs === null,
      dictionary: dictionary.name
    });
    try {
      // FIRST: Clear any existing game state completely
      setGameStarted(false);
      setRectangles([]);
      setGameItems([]);
      setGameQueue([]);
      setCompletedPairs(0);
      setMatchedItems(new Set());
      setDropZones([
        { id: 'upper', content: null },
        { id: 'lower', content: null }
      ]);
      setScore(0);
      setStreak(0);
      setCurrentTime(0);
      setSessionStats({ totalPairs: 0, totalAttempts: 0, correctMatches: 0 });

      const dictContent = dictionary.content || dictionary;
      validateAndConvertDictionary(dictContent);
      const maxPairsForGame = getMaxPairsInDictionary(dictContent);

      console.log('🔍 Assignment game setup:', { requiredPairs, maxPairsForGame });

      // ALWAYS start assignment games directly without config screen
      // Use the required pair count if provided, otherwise default to max pairs
      const pairCount = (requiredPairs !== undefined && requiredPairs !== null && typeof requiredPairs === 'number')
        ? Math.min(requiredPairs, maxPairsForGame)
        : maxPairsForGame;

      console.log('✅ Final pairCount decision:', { pairCount, usedRequiredPairs: pairCount !== maxPairsForGame });

      setRequiredPairCount(pairCount);
      setCurrentGameData(dictContent);
      setMaxPairs(maxPairsForGame);
      setSelectedPairCount(pairCount);
      setIsBuiltInGame(false);
      setIsAIGame(false);
      setIsSavedGame(false);

      const selectedItems = getRandomPairs(dictContent, pairCount);
      setGameItems(selectedItems);

      setGameQueue(selectedItems);

      const initialItems = selectInitialItems(selectedItems, pairCount);
      initializeRectangles(initialItems);

      setMessage(`📚 Assignment: Match ${pairCount} pairs`);
      setGameStarted(true); // Start immediately - no config screen ever for assignments
    } catch (error) {
      console.error('Assignment game processing error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setMessage(`❌ Failed to load assignment game: ${errorMessage}`);
    }
  };

  // Helper function to group items into pairs
  const createPairsFromItems = (items: string[]): [string, string][] => {
    const pairs: [string, string][] = [];
    for (let i = 0; i < items.length; i += 2) {
      if (i + 1 < items.length) {
        pairs.push([items[i], items[i + 1]]);
      }
    }
    return pairs;
  };

  // Count available pairs in current on-screen items
  const countAvailablePairs = (currentItems: string[], allGameItems: string[]): number => {
    const pairs = createPairsFromItems(allGameItems);
    let pairCount = 0;
    
    pairs.forEach(([item1, item2]) => {
      const hasItem1 = currentItems.includes(item1);
      const hasItem2 = currentItems.includes(item2);
      if (hasItem1 && hasItem2) {
        pairCount++;
      }
    });
    
    return pairCount;
  };

  // New algorithm for selecting initial items
  const selectInitialItems = (allItems: string[], pairCount: number): string[] => {
    // For sort mode, items are not paired - just select up to 10 items
    if (currentMatchingMode === 'sort') {
      // Show max 10 items initially
      return allItems.slice(0, Math.min(10, allItems.length));
    }

    // For category mode: items are already structured as triples [item1, item2, catName, ...]
    // Each match = 3 items, so show complete triples on screen
    if (currentMatchingMode === 'category') {
      const totalMatches = Math.floor(allItems.length / 3);

      if (totalMatches <= 4) {
        // Show all items
        return allItems.slice();
      }

      // Show 3 complete triples (9 items) + 1 extra triple partial
      // Items are already shuffled from getCategoryGameItems
      const itemsToShow = Math.min(allItems.length, 12); // 4 triples max
      return allItems.slice(0, itemsToShow);
    }

    // For one-to-one mode: use pair-based logic
    const pairs = createPairsFromItems(allItems);

    // ≤5 pairs: show all items
    if (pairCount <= 5) {
      return allItems.slice();
    }

    // ≥6 pairs: smart selection
    const initialItems: string[] = [];

    // Step 1: Select 3 complete pairs (6 items)
    const selectedPairs = pairs.slice(0, 3);
    selectedPairs.forEach(pair => {
      initialItems.push(...pair);
    });

    // Step 2: Select 4 random items from remaining pairs
    const remainingPairs = pairs.slice(3);
    const allRemainingItems = remainingPairs.flat();

    // Randomly select 4 items from remaining items
    const shuffledRemaining = [...allRemainingItems].sort(() => Math.random() - 0.5);
    initialItems.push(...shuffledRemaining.slice(0, 4));

    return initialItems;
  };

  const handleRefreshRectangles = () => {
    if (!gameStarted || rectangles.length === 0 || gameQueue.length === 0) return;

    // Clear any rectangles in drop zones back to game queue
    const itemsInZones: string[] = [];
    dropZones.forEach(zone => {
      if (zone.content) {
        itemsInZones.push(zone.content);
      }
    });

    // Clear drop zones based on current mode
    if (currentMatchingMode === 'category') {
      setDropZones([
        { id: 'item1', content: null, zoneType: 'item' },
        { id: 'item2', content: null, zoneType: 'item' },
        { id: 'category', content: null, zoneType: 'category' }
      ]);
    } else if (currentMatchingMode === 'sort') {
      // Sort mode uses label zones, just clear any content
      setLabelZones(prev => prev.map(z => ({ ...z, content: null })));
    } else {
      setDropZones([
        { id: 'upper', content: null, zoneType: 'default' },
        { id: 'lower', content: null, zoneType: 'default' }
      ]);
    }

    // Select items from gameQueue based on mode
    let newItems: string[];

    if (currentMatchingMode === 'category') {
      // Category mode: items are triples, show up to 4 triples (12 items)
      const totalTriples = Math.floor(gameQueue.length / 3);
      if (totalTriples <= 4) {
        newItems = gameQueue.slice();
      } else {
        newItems = gameQueue.slice(0, 12); // 4 complete triples
      }
    } else if (currentMatchingMode === 'sort') {
      // Sort mode: show up to 10 items
      newItems = gameQueue.slice(0, Math.min(10, gameQueue.length));
    } else {
      // One-to-one mode: use pair-based logic
      const pairs = createPairsFromItems(gameQueue);
      const pairCount = pairs.length;

      if (pairCount <= 5 || gameQueue.length <= 10) {
        newItems = gameQueue.slice();
      } else {
        newItems = [];
        const availablePairs = pairs.slice(0, Math.min(3, pairs.length));
        availablePairs.forEach(pair => {
          newItems.push(...pair);
        });
        const remainingPairs = pairs.slice(availablePairs.length);
        const allRemainingItems = remainingPairs.flat();
        const shuffledRemaining = [...allRemainingItems].sort(() => Math.random() - 0.5);
        const additionalCount = Math.min(4, allRemainingItems.length);
        newItems.push(...shuffledRemaining.slice(0, additionalCount));
      }
    }

    // Reinitialize rectangles with new selection
    initializeRectangles(newItems);

    // Show feedback
    setMessage('🔄 Rectangles refreshed!');
    createParticles(window.innerWidth / 2, window.innerHeight / 2, 15);
    if (soundEnabled) {
      soundManager.current.playDropSound();
    }
    if (hapticEnabled) {
      hapticManager.current.lightTap();
    }
  };

  const initializeRectangles = (items: string[]) => {
    const gameArea = getGameAreaDimensions(items.length);
    const rectDims = getRectangleDimensions(items.length);
    
    // Update state with current dimensions
    setCurrentGameAreaDimensions(gameArea);
    setCurrentRectangleDimensions(rectDims);
    
    // Calculate safe spawn area (game area minus rectangle dimensions) - now handled per-rectangle
    
    // Convert user speed setting (1-10) to base speed multiplier
    // Level 1: 0.3x, Level 5: 1.0x (default), Level 10: 2.5x
    const speedMultiplier = gameSpeed <= 5 
      ? 0.3 + (gameSpeed - 1) * 0.175  // 1->0.3, 5->1.0
      : 1.0 + (gameSpeed - 5) * 0.3;   // 5->1.0, 10->2.5
    
    // Adjust base speed based on rectangle count, then apply user speed setting
    const baseSpeedForCount = items.length > 18 ? 1.5 : items.length > 12 ? 2.0 : items.length > 6 ? 2.5 : 3.0;
    const baseSpeed = baseSpeedForCount * speedMultiplier;
    
    const newRectangles: MovingRectangle[] = items.map((item, index) => {
      const dynamicDims = getDynamicRectangleDimensions(item, items.length);
      const actualSafeWidth = gameArea.width - dynamicDims.width;
      const actualSafeHeight = gameArea.height - dynamicDims.height;
      
      // SLOWER SPEED for multi-line text rectangles
      const lineCount = dynamicDims.lines ? dynamicDims.lines.length : 1;
      const speedMultiplier = lineCount > 2 ? 0.2 : lineCount > 1 ? 0.4 : 1.0; // MUCH MUCH slower for 3+ lines, much slower for 2 lines
      const adjustedSpeed = baseSpeed * speedMultiplier;
      
      console.log('Speed adjustment for:', item, 'lines:', lineCount, 'baseSpeed:', baseSpeed, 'adjustedSpeed:', adjustedSpeed);
      
      return {
        id: `rect-${index}`,
        content: item,
        x: Math.random() * Math.max(actualSafeWidth, 0),
        y: Math.random() * Math.max(actualSafeHeight, 0),
        vx: (Math.random() - 0.5) * adjustedSpeed,
        vy: (Math.random() - 0.5) * adjustedSpeed,
        rotation: (Math.random() - 0.5) * 60, // -30 to 30 degrees for better readability
        scale: 0.95 + Math.random() * 0.1, // Less scale variation for consistency
        collisionEffect: false,
        width: dynamicDims.width,
        height: dynamicDims.height,
        fontSize: dynamicDims.fontSize,
        lines: dynamicDims.lines || [item],
        isLongContent: dynamicDims.isLongContent
      };
    });
    setRectangles(newRectangles);
  };



  // BULLETPROOF animation - runs completely outside React lifecycle
  useEffect(() => {
    if (!gameStarted) return;
    
    let animationRunning = true;
    
    const gameLoop = () => {
      if (!animationRunning) return;
      
      // Direct state updates that CANNOT be interrupted
      setRectangles(current => {
        if (current.length === 0) return current;
        
        // Get dimensions - but don't let this break animation
        let gameArea: { width: number; height: number };
        let rectDims: { width: number; height: number; fontSize?: string };
        try {
          gameArea = getGameAreaDimensions(current.length);
          rectDims = getRectangleDimensions(current.length);
        } catch {
          gameArea = { width: 600, height: 450 };
          rectDims = { width: 90, height: 50 };
        }
        
        let updated = current.map(rect => {
          // Use dynamic dimensions for each rectangle
          const rectWidth = rect.width || rectDims.width;
          const rectHeight = rect.height || rectDims.height;
          const maxX = gameArea.width - rectWidth;
          const maxY = gameArea.height - rectHeight;
          
          let newVx = rect.vx;
          let newVy = rect.vy;
          let newX = rect.x + newVx;
          let newY = rect.y + newVy;

          // Wall bouncing with minimal energy loss and speed boost
          if (newX <= 0 || newX >= maxX) {
            newVx = -newVx * 0.99; // Minimal energy loss
            newX = Math.max(0, Math.min(maxX, newX));
            // Add slight random perturbation to prevent exact reversals
            newVx += (Math.random() - 0.5) * 0.2;
          }
          if (newY <= 0 || newY >= maxY) {
            newVy = -newVy * 0.99; // Minimal energy loss
            newY = Math.max(0, Math.min(maxY, newY));
            // Add slight random perturbation to prevent exact reversals
            newVy += (Math.random() - 0.5) * 0.2;
          }

          // Ensure minimum speed to prevent getting stuck - adjusted by user speed setting
          const currentSpeed = Math.sqrt(newVx * newVx + newVy * newVy);
          const speedMultiplierRealTime = gameSpeed <= 5 
            ? 0.3 + (gameSpeed - 1) * 0.175  // 1->0.3, 5->1.0
            : 1.0 + (gameSpeed - 5) * 0.3;   // 5->1.0, 10->2.5
          const minSpeed = 0.5 * speedMultiplierRealTime;
          const maxSpeed = 2.5 * speedMultiplierRealTime; // Real-time speed adjustment
          
          if (currentSpeed < minSpeed && currentSpeed > 0) {
            // Boost speed while maintaining direction
            const speedMultiplier = minSpeed / currentSpeed;
            newVx *= speedMultiplier;
            newVy *= speedMultiplier;
          } else if (currentSpeed > maxSpeed) {
            // Cap maximum speed
            const speedMultiplier = maxSpeed / currentSpeed;
            newVx *= speedMultiplier;
            newVy *= speedMultiplier;
          } else if (currentSpeed === 0) {
            // If completely stopped, give random movement
            newVx = (Math.random() - 0.5) * 2;
            newVy = (Math.random() - 0.5) * 2;
          }

          return {
            ...rect,
            x: newX,
            y: newY,
            vx: newVx,
            vy: newVy,
          };
        });

        // Rectangle-to-rectangle collision detection
        for (let i = 0; i < updated.length; i++) {
          for (let j = i + 1; j < updated.length; j++) {
            const rect1 = updated[i];
            const rect2 = updated[j];
            
            // Use dynamic dimensions for collision detection
            const rect1Width = rect1.width || rectDims.width;
            const rect1Height = rect1.height || rectDims.height;
            const rect2Width = rect2.width || rectDims.width;
            const rect2Height = rect2.height || rectDims.height;
            
            // Calculate centers and distance
            const centerX1 = rect1.x + rect1Width / 2;
            const centerY1 = rect1.y + rect1Height / 2;
            const centerX2 = rect2.x + rect2Width / 2;
            const centerY2 = rect2.y + rect2Height / 2;
            
            const dx = centerX2 - centerX1;
            const dy = centerY2 - centerY1;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // Required minimum distance to prevent overlap (touching but not overlapping)
            const minDistance = (rect1Width + rect2Width) / 2 + (rect1Height + rect2Height) / 2;
            const touchDistance = minDistance * 0.6; // Allow them to get close but not overlap
            
            // Only apply gentle pushing if they're too close (no position jumping ever)
            if (distance > 0 && distance < touchDistance) {
              // Normalize direction vector
              const normalX = dx / distance;
              const normalY = dy / distance;
              
              // Very gentle repulsive force only through velocity, never position jumping
              const pushStrength = 0.05; // Very gentle push
              const pushX = -normalX * pushStrength;
              const pushY = -normalY * pushStrength;
              
              // Add gentle push to velocities (no position changes)
              updated[i] = {
                ...updated[i],
                vx: updated[i].vx + pushX,
                vy: updated[i].vy + pushY,
              };
              updated[j] = {
                ...updated[j],
                vx: updated[j].vx - pushX,
                vy: updated[j].vy - pushY,
              };
            }
          }
        }

        return updated;
      });
      
      // Continue loop
      requestAnimationFrame(gameLoop);
    };
    
    // Start immediately
    requestAnimationFrame(gameLoop);
    
    return () => {
      animationRunning = false;
    };
  }, [gameStarted, gameSpeed]); // Re-run when game starts or speed changes

  useEffect(() => {
    const savedHighScore = localStorage.getItem('nexusMatchHighScore');
    const savedBestTime = localStorage.getItem('nexusMatchBestTime');
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore));
    }
    if (savedBestTime) {
      setBestTime(parseInt(savedBestTime));
    }
  }, []);

  useEffect(() => {
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('nexusMatchHighScore', score.toString());
    }
  }, [score, highScore]);

  // Timer effect - reduced frequency to prevent flashing
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameStartTime && gameStarted && rectangles.length > 0) {
      interval = setInterval(() => {
        setCurrentTime(Date.now() - gameStartTime);
      }, 500); // Update every 500ms instead of 100ms
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [gameStartTime, gameStarted, rectangles.length]);

  // Success popup effect
  useEffect(() => {
    if (successPopup) {
      const timer = setTimeout(() => {
        setSuccessPopup(null);
      }, 1200); // Extended from 500ms to 1200ms
      return () => clearTimeout(timer);
    }
  }, [successPopup]);

  // Mismatch popup effect
  useEffect(() => {
    if (mismatchPopup) {
      const timer = setTimeout(() => {
        setMismatchPopup(null);
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [mismatchPopup]);

  // Handle window resize to update game area dimensions
  useEffect(() => {
    const handleResize = () => {
      if (rectangles.length > 0) {
        // Update current dimensions when window resizes
        const newGameArea = getGameAreaDimensions(rectangles.length);
        const newRectDims = getRectangleDimensions(rectangles.length);
        setCurrentGameAreaDimensions(newGameArea);
        setCurrentRectangleDimensions(newRectDims);

        // Reposition rectangles to fit within new bounds
        setRectangles(prevRects => prevRects.map(rect => ({
          ...rect,
          x: Math.min(rect.x, newGameArea.width - newRectDims.width),
          y: Math.min(rect.y, newGameArea.height - newRectDims.height)
        })));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [rectangles.length]);



  const handlePointerMove = (clientX: number, clientY: number) => {
    setDraggedRectangle(prev => {
      if (prev) {
        return {
          ...prev,
          x: clientX,
          y: clientY
        };
      }
      return prev;
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    handlePointerMove(e.clientX, e.clientY);
  };

  const handlePointerEnd = (clientX: number, clientY: number) => {
    setDraggedRectangle(prev => {
      if (prev) {
        // Check if dropped on a drop zone
        const dropZones = document.querySelectorAll('.drop-zone');
        let droppedOnZone: 'upper' | 'lower' | null = null;
        
        dropZones.forEach((zone) => {
          const rect = zone.getBoundingClientRect();
          if (clientX >= rect.left && clientX <= rect.right &&
              clientY >= rect.top && clientY <= rect.bottom) {
            droppedOnZone = zone.classList.contains('upper') ? 'upper' : 'lower';
          }
        });
        
        if (droppedOnZone) {
          // Handle drop success - need to call it asynchronously
          const zoneId = droppedOnZone; // Capture non-null value for closure
          setTimeout(() => {
            handleDropContent(prev.content, zoneId);
          }, 0);
        } else {
          // Failed drag - slide back to game area
          setTimeout(() => {
            slideRectangleBack(prev);
          }, 0);
        }
      }
      return null; // Always clear the dragged rectangle
    });
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    handlePointerEnd(e.clientX, e.clientY);
  };


  const spawnNewRectanglesWithExclusions = (completedPairsCount: number, excludedItems: Set<string>, currentQueue: string[], itemCount: number = 2) => {
    // Get current on-screen items
    const currentScreenItems = rectangles.map(r => r.content);

    // Get remaining items to potentially spawn
    // For category mode: don't use excludedItems Set since category names can repeat
    // Instead just use the queue (which already had matched items removed via indexOf)
    let remainingInQueue: string[];
    if (currentMatchingMode === 'category') {
      remainingInQueue = currentQueue.filter(item => !currentScreenItems.includes(item));
    } else {
      remainingInQueue = currentQueue.filter(item =>
        !excludedItems.has(item) && !currentScreenItems.includes(item)
      );
    }

    if (remainingInQueue.length === 0) return; // No more items to spawn

    const itemsToSpawn: string[] = [];

    // Category mode: game items are structured as triples [item1, item2, catName, ...]
    // Spawn the next complete triple from the queue
    if (currentMatchingMode === 'category') {
      // Spawn up to itemCount items (should be 3 for a full triple)
      const toSpawn = remainingInQueue.slice(0, Math.min(itemCount, remainingInQueue.length));
      itemsToSpawn.push(...toSpawn);

    } else {
      // One-to-one and sort modes
      const needToSpawn = itemCount;

      // Count current available pairs on screen
      const currentPairsOnScreen = countAvailablePairs(currentScreenItems, currentQueue);

      if (currentPairsOnScreen <= 2) {
        // Scenario 1: Need to ensure matchable pairs on screen
        // Add counterparts of current on-screen items first
        let counterpartsAdded = 0;
        for (const screenItem of currentScreenItems) {
          if (counterpartsAdded >= needToSpawn) break;

          if (currentGameData) {
            const partner = currentGameData[screenItem];
            if (partner && remainingInQueue.includes(partner) && !itemsToSpawn.includes(partner)) {
              itemsToSpawn.push(partner);
              counterpartsAdded++;
            }
          }
        }

        // Fill remaining slots with random items
        const remainingSlots = needToSpawn - itemsToSpawn.length;
        if (remainingSlots > 0) {
          const availableForRandom = remainingInQueue.filter(item => !itemsToSpawn.includes(item));
          const shuffled = [...availableForRandom].sort(() => Math.random() - 0.5);
          itemsToSpawn.push(...shuffled.slice(0, remainingSlots));
        }

      } else {
        // Scenario 2: Already have ≥3 pairs, add random items
        const shuffled = [...remainingInQueue].sort(() => Math.random() - 0.5);
        itemsToSpawn.push(...shuffled.slice(0, needToSpawn));
      }
    }
    // Create the new rectangles and spawn them
    if (itemsToSpawn.length > 0) {
      const gameArea = currentGameAreaDimensions;
      
      const newRectangles: MovingRectangle[] = itemsToSpawn.map((item, index) => {
        const dynamicDims = getDynamicRectangleDimensions(item, rectangles.length + itemsToSpawn.length);
        const actualSafeWidth = gameArea.width - dynamicDims.width;
        
        return {
          id: `rect-${Date.now()}-${index}`,
          content: item,
          x: Math.random() * Math.max(actualSafeWidth, 0),
          y: -dynamicDims.height - (index * 50), // Start above screen with offset
          vx: (Math.random() - 0.5) * 2.5,
          vy: 3.0 + Math.random() * 2.0, // Falling down with better speed
          rotation: (Math.random() - 0.5) * 60, // -30 to 30 degrees for better readability
          scale: 0.95 + Math.random() * 0.1,
          collisionEffect: false,
          width: dynamicDims.width,
          height: dynamicDims.height,
          fontSize: dynamicDims.fontSize,
          lines: dynamicDims.lines || [item]
        };
      });
      
      setRectangles(prev => [...prev, ...newRectangles]);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const spawnNewRectangles = (completedPairsCount: number) => {
    // Get current on-screen items
    const currentScreenItems = rectangles.map(r => r.content);
    
    // Get all remaining items not yet matched and not currently on screen
    const remainingInQueue = gameQueue.filter(item => 
      !matchedItems.has(item) && !currentScreenItems.includes(item)
    );
    
    if (remainingInQueue.length === 0) return; // No more items to spawn
    
    // We need to spawn exactly 2 items to replace the matched pair
    const needToSpawn = 2;
    
    // Count current available pairs on screen using the new algorithm
    const currentPairsOnScreen = countAvailablePairs(currentScreenItems, gameQueue);
    
    const itemsToSpawn: string[] = [];
    
    if (currentPairsOnScreen <= 2) {
      // Scenario 1: Need to ensure at least 3 pairs on screen
      // Add counterparts of current on-screen items first (up to 2 items)
      let counterpartsAdded = 0;
      for (const screenItem of currentScreenItems) {
        if (counterpartsAdded >= needToSpawn) break;
        
        if (currentGameData) {
          const partner = currentGameData[screenItem];
          if (partner && remainingInQueue.includes(partner) && !itemsToSpawn.includes(partner)) {
            itemsToSpawn.push(partner);
            counterpartsAdded++;
          }
        }
      }
      
      // Fill remaining slots with random items
      const remainingSlots = needToSpawn - itemsToSpawn.length;
      if (remainingSlots > 0) {
        const availableForRandom = remainingInQueue.filter(item => !itemsToSpawn.includes(item));
        const shuffled = [...availableForRandom].sort(() => Math.random() - 0.5);
        itemsToSpawn.push(...shuffled.slice(0, remainingSlots));
      }
      
    } else {
      // Scenario 2: Already have ≥3 pairs, add 2 random items
      const shuffled = [...remainingInQueue].sort(() => Math.random() - 0.5);
      itemsToSpawn.push(...shuffled.slice(0, needToSpawn));
    }
    
    // Create the new rectangles and spawn them
    if (itemsToSpawn.length > 0) {
      const gameArea = currentGameAreaDimensions;
      
      const newRectangles: MovingRectangle[] = itemsToSpawn.map((item, index) => {
        const dynamicDims = getDynamicRectangleDimensions(item, rectangles.length + itemsToSpawn.length);
        const actualSafeWidth = gameArea.width - dynamicDims.width;
        
        return {
          id: `rect-${Date.now()}-${index}`,
          content: item,
          x: Math.random() * Math.max(actualSafeWidth, 0),
          y: -dynamicDims.height - (index * 50), // Start above screen with offset
          vx: (Math.random() - 0.5) * 2.5,
          vy: 3.0 + Math.random() * 2.0, // Falling down with better speed
          rotation: (Math.random() - 0.5) * 60, // -30 to 30 degrees for better readability
          scale: 0.95 + Math.random() * 0.1,
          collisionEffect: false,
          width: dynamicDims.width,
          height: dynamicDims.height,
          fontSize: dynamicDims.fontSize,
          lines: dynamicDims.lines || [item]
        };
      });
      
      setRectangles(prev => [...prev, ...newRectangles]);
    }
  };

  const slideRectangleBack = (draggedRect: { content: string; x: number; y: number; rotation: number; scale: number }) => {
    // Try to restore from saved state, otherwise create new
    const savedRect = savedRectangleStates.find(r => r.content === draggedRect.content);
    
    let newRect: MovingRectangle;
    
    if (savedRect) {
      // Restore with preserved velocity
      newRect = {
        ...savedRect,
        id: `rect-${Date.now()}-${Math.random()}`, // New ID to avoid conflicts
      };
    } else {
      // Fallback to random position if no saved state
      const gameArea = currentGameAreaDimensions;
      const dynamicDims = getDynamicRectangleDimensions(draggedRect.content, rectangles.length + 1);
      const safeWidth = gameArea.width - dynamicDims.width;
      const safeHeight = gameArea.height - dynamicDims.height;
      
      newRect = {
        id: `rect-${Date.now()}-${Math.random()}`,
        content: draggedRect.content,
        x: Math.random() * Math.max(safeWidth, 0),
        y: Math.random() * Math.max(safeHeight, 0),
        vx: (Math.random() - 0.5) * 2.5,
        vy: (Math.random() - 0.5) * 2.5,
        width: dynamicDims.width,
        height: dynamicDims.height,
        fontSize: dynamicDims.fontSize,
        lines: dynamicDims.lines || [draggedRect.content],
        isLongContent: dynamicDims.isLongContent,
        rotation: draggedRect.rotation,
        scale: draggedRect.scale,
        collisionEffect: false,
      };
    }
    
    setRectangles(prev => [...prev, newRect]);
    
    // Clear saved states after use
    setSavedRectangleStates([]);
  };

  const handleDropContent = async (content: string, zoneId: string) => {
    console.log('handleDropContent called with:', content, zoneId);
    try {
      // Clear saved states since drop was successful
      setSavedRectangleStates([]);

      if (!content || content.trim() === '') {
        console.warn('Empty content dropped, ignoring');
        return;
      }

      if (soundEnabled) {
        soundManager.current.playDropSound();
      }
      if (hapticEnabled) {
        hapticManager.current.lightTap();
      }

      // Sort mode: Check match immediately (one item at a time)
      if (currentMatchingMode === 'sort') {
        console.log('Sort mode handleDropContent called:', { content, zoneId, labelZones, currentMatchingMode });

        // Find the label zone that was dropped on
        const zoneIndex = labelZones.findIndex(z => z.id === zoneId);
        if (zoneIndex === -1) {
          console.warn('Invalid label zone - zone not found:', { zoneId, availableZones: labelZones.map(z => z.id) });
          return;
        }

        // Create temporary zones array with this item
        const tempZones = labelZones.map((z, i) =>
          i === zoneIndex ? { ...z, content } : z
        );

        // Debug logging
        console.log('Sort mode drop:', {
          content,
          targetLabel: labelZones[zoneIndex].labelName,
          currentGameData,
          tempZones
        });

        // Check match immediately
        const result = checkMatchLogic(tempZones as any, currentGameData);
        console.log('Sort match result:', result);

        // Track attempts for competitive mode
        if (gameMode === 'competition' && competitiveGameActive) {
          handleCompetitiveMatchAttempt(result.isMatch);
        }

        if (result.isMatch) {
          // Success: Remove item from game
          if (draggedRectangle) {
            setRectangles(prev => prev.filter(rect => rect.id !== draggedRectangle.id));
          } else {
            setRectangles(prev => {
              const index = prev.findIndex(rect => rect.content === content);
              if (index !== -1) {
                return [...prev.slice(0, index), ...prev.slice(index + 1)];
              }
              return prev;
            });
          }

          setMatchedItems(prev => new Set([...Array.from(prev), content]));
          setScore(prev => prev + 1);
          setCompletedPairs(prev => prev + 1);
          setSessionStats(prev => ({
            totalPairs: prev.totalPairs + 1,
            totalAttempts: prev.totalAttempts + 1,
            correctMatches: prev.correctMatches + 1
          }));

          // Show success feedback
          if (soundEnabled) {
            soundManager.current.playMatchSound();
          }
          if (hapticEnabled) {
            hapticManager.current.success();
          }
          setMessage(`✓ ${content} → ${labelZones[zoneIndex].labelName}!`);
          setSuccessPopup(`✓ +1 point`);
          createParticles(window.innerWidth / 2, window.innerHeight / 2, 15);

          // Progressive spawning: add new rectangles when items are matched
          const newCompletedPairs = completedPairs + 1;
          const totalPairsNeeded = selectedPairCount;

          // Only spawn new rectangles if we haven't completed all pairs yet
          if (newCompletedPairs < totalPairsNeeded) {
            // Get updated matched items and game queue for spawning
            const updatedMatchedItems = new Set([...Array.from(matchedItems), content]);
            const updatedQueue = gameQueue.filter(item => item !== content);
            // Sort mode: spawn 1 item to replace the 1 matched item
            spawnNewRectanglesWithExclusions(newCompletedPairs, updatedMatchedItems, updatedQueue, 1);
          }

          // Check completion
          if (completedPairs + 1 >= selectedPairCount) {
            handleGameCompletion();
          }
        } else {
          // Wrong: Bounce back with penalty
          setScore(prev => Math.max(0, prev - 1));
          setSessionStats(prev => ({
            ...prev,
            totalAttempts: prev.totalAttempts + 1
          }));

          if (soundEnabled) {
            soundManager.current.playMismatchSound();
          }
          if (hapticEnabled) {
            hapticManager.current.error();
          }
          setMessage(`✗ ${content} ≠ ${labelZones[zoneIndex].labelName}`);
          setMismatchPopup('✗ Wrong label!');

          // Flash zone red and restore item
          setTimeout(() => {
            restoreItemsToGameArea([content]);
            setMismatchPopup(null);
          }, 800);
        }
        return; // Exit early for sort mode
      }

      // Category and one-to-one modes: Fill zones first, then check
      const updatedZones = dropZones.map(zone =>
        zone.id === zoneId ? { ...zone, content } : zone
      );
      setDropZones(updatedZones);

      // Rectangle was already removed when dragging started, no need to filter again

      // Check if all zones are filled based on the current matching mode
      if (currentMatchingMode === 'category') {
        // Category mode: need item1, item2, and category filled
        const item1 = updatedZones.find(z => z.id === 'item1')?.content;
        const item2 = updatedZones.find(z => z.id === 'item2')?.content;
        const category = updatedZones.find(z => z.id === 'category')?.content;

        if (item1 && item2 && category &&
            item1.trim() !== '' && item2.trim() !== '' && category.trim() !== '') {
          await checkMatchWithZones(updatedZones);
        }
      } else {
        // One-to-one mode: need upper and lower filled
        if (updatedZones[0].content && updatedZones[1].content &&
            updatedZones[0].content.trim() !== '' && updatedZones[1].content.trim() !== '') {
          await checkMatch(updatedZones[0].content, updatedZones[1].content);
        }
      }
    } catch (error) {
      console.warn('Error in drop handler:', error);
    }
  };


  const handleDrop = async (e: React.DragEvent, zoneId: string) => {
    try {
      e.preventDefault();
      const content = e.dataTransfer.getData('text/plain');

      // Enhanced safety checks
      if (!content || content.trim() === '') {
        console.warn('Empty content dropped, ignoring');
        return;
      }

      // CRITICAL: Validate that this is a legitimate rectangle content
      // Check if it's currently being dragged or exists in rectangles
      const isValidRectangle = rectangles.some(rect => rect.content === content) ||
                               (draggedRectangle && draggedRectangle.content === content);
      if (!isValidRectangle) {
        console.warn('Invalid content dropped - not a game rectangle, ignoring:', content);
        return;
      }

      if (soundEnabled) {
        soundManager.current.playDropSound();
      }
      if (hapticEnabled) {
        hapticManager.current.lightTap();
      }

      // Sort mode: Check match immediately (one item at a time)
      if (currentMatchingMode === 'sort') {
        console.log('Sort mode handleDropContent called:', { content, zoneId, labelZones, currentMatchingMode });

        // Find the label zone that was dropped on
        const zoneIndex = labelZones.findIndex(z => z.id === zoneId);
        if (zoneIndex === -1) {
          console.warn('Invalid label zone - zone not found:', { zoneId, availableZones: labelZones.map(z => z.id) });
          return;
        }

        // Create temporary zones array with this item
        const tempZones = labelZones.map((z, i) =>
          i === zoneIndex ? { ...z, content } : z
        );

        // Debug logging
        console.log('Sort mode drop:', {
          content,
          targetLabel: labelZones[zoneIndex].labelName,
          currentGameData,
          tempZones
        });

        // Check match immediately
        const result = checkMatchLogic(tempZones as any, currentGameData);
        console.log('Sort match result:', result);

        // Track attempts for competitive mode
        if (gameMode === 'competition' && competitiveGameActive) {
          handleCompetitiveMatchAttempt(result.isMatch);
        }

        if (result.isMatch) {
          // Success: Remove item from game
          if (draggedRectangle) {
            setRectangles(prev => prev.filter(rect => rect.id !== draggedRectangle.id));
          } else {
            setRectangles(prev => {
              const index = prev.findIndex(rect => rect.content === content);
              if (index !== -1) {
                return [...prev.slice(0, index), ...prev.slice(index + 1)];
              }
              return prev;
            });
          }

          setMatchedItems(prev => new Set([...Array.from(prev), content]));
          setScore(prev => prev + 1);
          setCompletedPairs(prev => prev + 1);
          setSessionStats(prev => ({
            totalPairs: prev.totalPairs + 1,
            totalAttempts: prev.totalAttempts + 1,
            correctMatches: prev.correctMatches + 1
          }));

          // Show success feedback
          if (soundEnabled) {
            soundManager.current.playMatchSound();
          }
          if (hapticEnabled) {
            hapticManager.current.success();
          }
          setMessage(`✓ ${content} → ${labelZones[zoneIndex].labelName}!`);
          setSuccessPopup(`✓ +1 point`);
          createParticles(window.innerWidth / 2, window.innerHeight / 2, 15);

          // Progressive spawning: add new rectangles when items are matched
          const newCompletedPairs = completedPairs + 1;
          const totalPairsNeeded = selectedPairCount;

          // Only spawn new rectangles if we haven't completed all pairs yet
          if (newCompletedPairs < totalPairsNeeded) {
            // Get updated matched items and game queue for spawning
            const updatedMatchedItems = new Set([...Array.from(matchedItems), content]);
            const updatedQueue = gameQueue.filter(item => item !== content);
            // Sort mode: spawn 1 item to replace the 1 matched item
            spawnNewRectanglesWithExclusions(newCompletedPairs, updatedMatchedItems, updatedQueue, 1);
          }

          // Check completion
          if (completedPairs + 1 >= selectedPairCount) {
            handleGameCompletion();
          }
        } else {
          // Wrong: Bounce back with penalty
          setScore(prev => Math.max(0, prev - 1));
          setSessionStats(prev => ({
            ...prev,
            totalAttempts: prev.totalAttempts + 1
          }));

          if (soundEnabled) {
            soundManager.current.playMismatchSound();
          }
          if (hapticEnabled) {
            hapticManager.current.error();
          }
          setMessage(`✗ ${content} ≠ ${labelZones[zoneIndex].labelName}`);
          setMismatchPopup('✗ Wrong label!');

          // Flash zone red and restore item
          setTimeout(() => {
            restoreItemsToGameArea([content]);
            setMismatchPopup(null);
          }, 800);
        }
        return; // Exit early for sort mode
      }

      // Category and one-to-one modes: Fill zones first, then check
      const updatedZones = dropZones.map(zone =>
        zone.id === zoneId ? { ...zone, content } : zone
      );
      setDropZones(updatedZones);

      // Remove the dragged rectangle by ID if available, otherwise by content (fallback for legacy drag/drop)
      if (draggedRectangle) {
        setRectangles(prev => prev.filter(rect => rect.id !== draggedRectangle.id));
      } else {
        // Fallback: This should rarely happen now that we use pointer events
        // But kept for safety - find first rectangle with matching content
        setRectangles(prev => {
          const index = prev.findIndex(rect => rect.content === content);
          if (index !== -1) {
            return [...prev.slice(0, index), ...prev.slice(index + 1)];
          }
          return prev;
        });
      }

      // Check if all zones are filled based on the current matching mode
      if (currentMatchingMode === 'category') {
        // Category mode: need item1, item2, and category filled
        const item1 = updatedZones.find(z => z.id === 'item1')?.content;
        const item2 = updatedZones.find(z => z.id === 'item2')?.content;
        const category = updatedZones.find(z => z.id === 'category')?.content;

        if (item1 && item2 && category &&
            item1.trim() !== '' && item2.trim() !== '' && category.trim() !== '') {
          await checkMatchWithZones(updatedZones);
        }
      } else {
        // One-to-one mode: need upper and lower filled
        if (updatedZones[0].content && updatedZones[1].content &&
            updatedZones[0].content.trim() !== '' && updatedZones[1].content.trim() !== '') {
          await checkMatch(updatedZones[0].content, updatedZones[1].content);
        }
      }
    } catch (error) {
      console.warn('Error in drop handler:', error);
    }
  };

  // New function to check match using zone-based logic (supports both modes)
  const checkMatchWithZones = async (zones: DropZone[]) => {
    if (!currentGameData) {
      console.warn('No game data available for matching');
      return;
    }

    // Use the utility function to check the match
    const result = checkMatchLogic(zones as any, currentGameData);

    // Track attempts for competitive mode
    if (gameMode === 'competition' && competitiveGameActive) {
      handleCompetitiveMatchAttempt(result.isMatch);
    }

    if (result.isMatch) {
      // Get all filled items from zones
      const matchedItemsList = getFilledItems(zones as any);
      const points = result.pointsAwarded;
      const itemCount = result.itemsMatched;

      const newStreak = streak + 1;
      setScore(prev => prev + points);
      setStreak(newStreak);
      setCompletedPairs(prev => prev + 1); // Each match (regardless of item count) = 1 completed pair

      // Update session stats
      setSessionStats(prev => ({
        totalPairs: prev.totalPairs + 1,
        totalAttempts: prev.totalAttempts + 1,
        correctMatches: prev.correctMatches + 1
      }));

      // Remove matched items from queue
      const updatedQueue = [...gameQueue];
      matchedItemsList.forEach(item => {
        const index = updatedQueue.indexOf(item);
        if (index !== -1) {
          updatedQueue.splice(index, 1);
        }
      });
      setGameQueue(updatedQueue);

      // Track matched items to prevent re-spawning
      const updatedMatchedItems = new Set(matchedItems);
      matchedItemsList.forEach(item => updatedMatchedItems.add(item));
      setMatchedItems(updatedMatchedItems);

      // Clear zones
      if (currentMatchingMode === 'category') {
        setDropZones([
          { id: 'item1', content: null, zoneType: 'item' },
          { id: 'item2', content: null, zoneType: 'item' },
          { id: 'category', content: null, zoneType: 'category' }
        ]);
      } else {
        setDropZones([
          { id: 'upper', content: null, zoneType: 'default' },
          { id: 'lower', content: null, zoneType: 'default' }
        ]);
      }

      setMessage(`🎉 ${result.message || 'Perfect Match!'} +${points} points | Streak: ${newStreak}`);
      setSuccessPopup(`✨ +${points} points! ${newStreak > 1 ? `Streak: ${newStreak}` : ''}`);

      createParticles(window.innerWidth / 2, window.innerHeight / 2, 25);
      if (soundEnabled) {
        soundManager.current.playMatchSound();
        if (newStreak > 1) {
          soundManager.current.playStreakSound(newStreak);
        }
      }
      if (hapticEnabled) {
        hapticManager.current.success();
      }

      // Progressive spawning
      const newCompletedPairs = completedPairs + 1;

      if (newCompletedPairs < selectedPairCount) {
        // Spawn the same number of items that were just matched (2 for one-to-one, 3 for category)
        spawnNewRectanglesWithExclusions(newCompletedPairs, updatedMatchedItems, updatedQueue, itemCount);
      }

      // Check completion
      if (newCompletedPairs >= selectedPairCount) {
        handleGameCompletion();
      }
    } else {
      // Wrong match
      setStreak(0);
      setScore(prev => Math.max(0, prev - 1));

      setSessionStats(prev => ({
        ...prev,
        totalAttempts: prev.totalAttempts + 1
      }));

      setMessage(`❌ ${result.message || 'Wrong match!'} -1 point`);
      setMismatchPopup('❌ Wrong match!');

      if (soundEnabled) {
        soundManager.current.playMismatchSound();
      }
      if (hapticEnabled) {
        hapticManager.current.error();
      }

      // Return items to game area
      const itemsToReturn = getFilledItems(zones as any);
      setTimeout(() => {
        restoreItemsToGameArea(itemsToReturn);

        // Clear zones
        if (currentMatchingMode === 'category') {
          setDropZones([
            { id: 'item1', content: null, zoneType: 'item' },
            { id: 'item2', content: null, zoneType: 'item' },
            { id: 'category', content: null, zoneType: 'category' }
          ]);
        } else {
          setDropZones([
            { id: 'upper', content: null, zoneType: 'default' },
            { id: 'lower', content: null, zoneType: 'default' }
          ]);
        }

        setMismatchPopup(null);
      }, 1500);
    }
  };

  // Helper function to handle game completion
  const handleGameCompletion = () => {
    if (gameStartTime) {
      const completionTime = Date.now() - gameStartTime;
      if (!bestTime || completionTime < bestTime) {
        setBestTime(completionTime);
        localStorage.setItem('nexusMatchBestTime', completionTime.toString());
        setSuccessPopup(`🏆 New Best Time! ${(completionTime / 1000).toFixed(1)}s`);
      } else {
        setSuccessPopup(`🎮 Level Complete! Time: ${(completionTime / 1000).toFixed(1)}s`);
      }

      // Record assignment attempt if this is an assignment
      if (currentAssignmentId && currentGameId) {
        const accuracy = sessionStats.totalAttempts > 0
          ? Math.round((sessionStats.correctMatches / sessionStats.totalAttempts) * 100)
          : 100;
        apiService.recordAssignmentAttempt(currentAssignmentId, {
          gameId: currentGameId,
          score,
          accuracy,
          correctMatches: sessionStats.correctMatches,
          wrongMatches: sessionStats.totalAttempts - sessionStats.correctMatches,
          timeSpent: Math.round(completionTime / 1000)
        });
      }

      // Competitive mode completion
      if (gameMode === 'competition' && competitiveGameActive) {
        handleCompetitiveGameComplete();
      } else {
        // Single player: show result screen
        setSinglePlayerCompletionTime(completionTime);
        setShowSinglePlayerResults(true);
        setGameStarted(false);
      }
    }
  };

  // Helper function to restore items to game area
  const restoreItemsToGameArea = (items: string[]) => {
    const gameArea = document.querySelector('.game-area');
    if (!gameArea) return;

    const rect = gameArea.getBoundingClientRect();
    const newRectangles: MovingRectangle[] = items.map((item, index) => ({
      id: `restored-${Date.now()}-${index}`,
      content: item,
      x: rect.width * 0.3 + Math.random() * rect.width * 0.4,
      y: rect.height * 0.3 + Math.random() * rect.height * 0.4,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      rotation: Math.random() * 10 - 5,
      scale: 1,
      collisionEffect: false
    }));

    setRectangles(prev => [...prev, ...newRectangles]);
  };

  const checkClientSideMatch = (item1: string, item2: string, dictionary: Record<string, string>): boolean => {
    // Check if item1 maps to item2 or item2 maps to item1
    return dictionary[item1] === item2 || dictionary[item2] === item1;
  };

  const checkMatch = async (item1: string, item2: string) => {
    // Validate inputs
    if (!item1 || !item2 || item1.trim() === '' || item2.trim() === '') {
      console.warn('Invalid items for matching:', { item1, item2 });
      return;
    }

    let result: { isMatch: boolean };

    try {
      if (currentGameData) {
        // Use client-side matching when game data is available
        result = { isMatch: checkClientSideMatch(item1, item2, currentGameData) };
      } else {
        // Fallback to server-side matching (shouldn't happen with new client-side processing)
        const response = await fetch(`${API_BASE_URL}/api/check-match`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ item1, item2 }),
        });
        result = await response.json();
      }

      // Track attempts for competitive mode
      if (gameMode === 'competition' && competitiveGameActive) {
        handleCompetitiveMatchAttempt(result.isMatch);
      }

      if (result.isMatch) {
        const newStreak = streak + 1;
        const points = 2; // Fixed +2 points for successful match
        setScore(prev => prev + points);
        setStreak(newStreak);
        setCompletedPairs(prev => prev + 1); // Track completed pairs
        
        // Update session stats
        setSessionStats(prev => ({
          totalPairs: prev.totalPairs + 1,
          totalAttempts: prev.totalAttempts + 1,
          correctMatches: prev.correctMatches + 1
        }));
        
        // Remove the matched pair from the queue so they don't reappear
        // Handle identical pairs (e.g., "in" = "in") by removing exactly 2 instances
        const updatedQueue = [...gameQueue];
        let removedCount = 0;
        const maxToRemove = 2;

        // Remove first occurrence of item1
        const index1 = updatedQueue.indexOf(item1);
        if (index1 !== -1) {
          updatedQueue.splice(index1, 1);
          removedCount++;
        }

        // Remove first occurrence of item2 (might be same as item1 for identical pairs)
        if (removedCount < maxToRemove) {
          const index2 = updatedQueue.indexOf(item2);
          if (index2 !== -1) {
            updatedQueue.splice(index2, 1);
          }
        }

        setGameQueue(updatedQueue);
        
        // Track matched items to prevent re-spawning
        const updatedMatchedItems = new Set(matchedItems);
        updatedMatchedItems.add(item1);
        updatedMatchedItems.add(item2);
        setMatchedItems(updatedMatchedItems);
        
        setMessage(`🎉 Perfect Match! +${points} points | Streak: ${newStreak}`);
        
        // Show success popup
        setSuccessPopup(`✨ +${points} points! ${newStreak > 1 ? `Streak: ${newStreak}` : ''}`);
        
        createParticles(window.innerWidth / 2, window.innerHeight / 2, 25);
        if (soundEnabled) {
          soundManager.current.playMatchSound();
          if (newStreak > 1) {
            soundManager.current.playStreakSound(newStreak);
          }
        }
        if (hapticEnabled) {
          hapticManager.current.success();
        }
        
        // Progressive spawning: add new rectangles when pairs are cleared
        const newCompletedPairs = completedPairs + 1;
        const totalPairsNeeded = selectedPairCount;

        // Only spawn new rectangles if we haven't completed all pairs yet
        if (newCompletedPairs < totalPairsNeeded) {
          // One-to-one mode: spawn 2 items to replace the 2 matched items
          spawnNewRectanglesWithExclusions(newCompletedPairs, updatedMatchedItems, updatedQueue, 2);
        }
        
        // Check if all pairs are completed (not just screen rectangles)
        
        if (newCompletedPairs >= totalPairsNeeded) {
          // Level completed - record time
          if (gameStartTime) {
            const completionTime = Date.now() - gameStartTime;
            if (!bestTime || completionTime < bestTime) {
              setBestTime(completionTime);
              localStorage.setItem('nexusMatchBestTime', completionTime.toString());
              setSuccessPopup(`🏆 New Best Time! ${(completionTime / 1000).toFixed(1)}s`);
            } else {
              setSuccessPopup(`🏆 Level Complete! Time: ${(completionTime / 1000).toFixed(1)}s`);
            }
          }

          // Handle competitive mode completion
          if (gameMode === 'competition' && competitiveGameActive) {
            handleCompetitiveGameComplete();
          }

          // Handle assignment game completion - record attempt
          if (currentAssignmentId && currentGameId && gameMode === 'freeplay') {
            const finalPairsCompleted = completedPairs + 1; // Include this final pair

            // Only record if all required pairs were completed
            if (requiredPairCount === null || finalPairsCompleted >= requiredPairCount) {
              const completionTime = gameStartTime ? Date.now() - gameStartTime : 0;
              const finalCorrectMatches = sessionStats.correctMatches + 1;
              const finalTotalAttempts = sessionStats.totalAttempts + 1;
              const finalAccuracy = Math.round((finalCorrectMatches / finalTotalAttempts) * 100);

              apiService.recordAssignmentAttempt(currentAssignmentId, {
                gameId: currentGameId,
                score: score + 2, // Add the final 2 points from this match
                accuracy: finalAccuracy,
                correctMatches: finalCorrectMatches,
                wrongMatches: finalTotalAttempts - finalCorrectMatches,
                timeSpent: Math.round(completionTime / 1000) // Convert to seconds
              }).then(() => {
                console.log('✅ Assignment attempt recorded successfully');
              }).catch((error) => {
                console.error('❌ Failed to record assignment attempt:', error);
              });
            } else {
              console.log(`⚠️ Not recording attempt - only completed ${finalPairsCompleted}/${requiredPairCount} required pairs`);
            }
          }

          if (gameMode === 'freeplay') {
            // Calculate final accuracy and pair count including this match
            const finalPairsCompleted = completedPairs + 1;
            const finalCorrectMatches = sessionStats.correctMatches + 1;
            const finalTotalAttempts = sessionStats.totalAttempts + 1;
            const finalAccuracy = Math.round((finalCorrectMatches / finalTotalAttempts) * 100);
            if (isBuiltInGame) {
              setMessage(`🎉 Great job! Completed ${finalPairsCompleted}/${selectedPairCount} pairs with ${finalAccuracy}% accuracy (${finalCorrectMatches}/${finalTotalAttempts})! Click "← Games" to try another game.`);
            } else {
              setMessage(`🎉 Great job! Completed ${finalPairsCompleted}/${selectedPairCount} pairs with ${finalAccuracy}% accuracy (${finalCorrectMatches}/${finalTotalAttempts})!`);
            }
          } else {
            if (isBuiltInGame) {
              setMessage('🏆 Level Complete! Click "← Games" to try another game.');
            } else {
              setMessage('🏆 Level Complete! Upload a new game file to continue!');
            }
          }
          createParticles(window.innerWidth / 2, window.innerHeight / 2, 50);
          if (soundEnabled) {
            soundManager.current.playLevelCompleteSound();
          }
          if (hapticEnabled) {
            hapticManager.current.levelComplete();
          }
        }
        
        setDropZones([
          { id: 'upper', content: null },
          { id: 'lower', content: null }
        ]);
      } else {
        setStreak(0);
        setMessage('💥 Not a match! Streak reset. Try again!');
        
        // Update session stats for incorrect attempt and deduct points
        setScore(prev => Math.max(0, prev - 1)); // -1 point, minimum 0
        setSessionStats(prev => ({
          ...prev,
          totalAttempts: prev.totalAttempts + 1
        }));
        
        // Show mismatch popup
        setMismatchPopup('💥 No Match! -1 Point');
        
        if (soundEnabled) {
          soundManager.current.playMismatchSound();
        }
        if (hapticEnabled) {
          hapticManager.current.error();
        }
        setTimeout(() => {
          // Use the parameters directly instead of reading from state
          // to avoid stale closure issues
          const itemsToRestore: MovingRectangle[] = [];
          const gameArea = currentGameAreaDimensions;
          
          // Safety check: only restore items that have valid content
          if (item1 && item1.trim() !== '') {
            const dynamicDims1 = getDynamicRectangleDimensions(item1, rectangles.length + itemsToRestore.length + 1);
            itemsToRestore.push({
              id: `rect-${Date.now()}-${Math.random()}`,
              content: item1,
              x: Math.random() * Math.max(gameArea.width - dynamicDims1.width, 50),
              y: Math.random() * Math.max(gameArea.height - dynamicDims1.height, 50),
              vx: (Math.random() - 0.5) * 2,
              vy: (Math.random() - 0.5) * 2,
              rotation: (Math.random() - 0.5) * 60, // -30 to 30 degrees for better readability
              scale: 0.8 + Math.random() * 0.4,
              collisionEffect: false,
              width: dynamicDims1.width,
              height: dynamicDims1.height,
              fontSize: dynamicDims1.fontSize,
              lines: dynamicDims1.lines || [item1]
            });
          }
          
          // Safety check: only restore items that have valid content
          if (item2 && item2.trim() !== '') {
            const dynamicDims2 = getDynamicRectangleDimensions(item2, rectangles.length + itemsToRestore.length + 1);
            itemsToRestore.push({
              id: `rect-${Date.now() + 1}-${Math.random()}`,
              content: item2,
              x: Math.random() * Math.max(gameArea.width - dynamicDims2.width, 50),
              y: Math.random() * Math.max(gameArea.height - dynamicDims2.height, 50),
              vx: (Math.random() - 0.5) * 2,
              vy: (Math.random() - 0.5) * 2,
              rotation: (Math.random() - 0.5) * 60, // -30 to 30 degrees for better readability
              scale: 0.8 + Math.random() * 0.4,
              collisionEffect: false,
              width: dynamicDims2.width,
              height: dynamicDims2.height,
              fontSize: dynamicDims2.fontSize,
              lines: dynamicDims2.lines || [item2]
            });
          }
          
          // Only add items back if we have valid items to restore
          if (itemsToRestore.length > 0) {
            setRectangles(prev => [...prev, ...itemsToRestore]);
          }
          
          // Clear drop zones
          setDropZones([
            { id: 'upper', content: null },
            { id: 'lower', content: null }
          ]);
        }, 1500);
      }
    } catch (error) {
      setMessage('Error checking match');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadDictionary(file);
      // Auto-collapse built-in games panel when uploading custom JSON
      setShowBuiltInGames(false);
    }
  };


  const startUploadedGame = (pairCount: number) => {
    if (!uploadedDictionary) return;
    
    const { gameDataMapping, message } = uploadedDictionary;
    
    // Reset game state
    setScore(0);
    setStreak(0);
    setCurrentTime(0);

    // Detect matching mode
    const gameMatchingMode = detectMatchingMode(gameDataMapping);
    setCurrentMatchingMode(gameMatchingMode);

    // Set up drop zones based on matching mode
    if (gameMatchingMode === 'sort') {
      // Sort mode: Create dynamic label zones
      const labels = gameDataMapping.labels || [];
      const zones = labels.map((label: string, index: number) => ({
        id: `label-${index}`,
        content: null,
        zoneType: 'label' as const,
        labelName: label
      }));
      setLabelZones(zones);
      setDropZones([]); // No traditional drop zones in sort mode
    } else if (gameMatchingMode === 'category') {
      setDropZones([
        { id: 'item1', content: null, zoneType: 'item' },
        { id: 'item2', content: null, zoneType: 'item' },
        { id: 'category', content: null, zoneType: 'category' }
      ]);
      setLabelZones([]); // No label zones in category mode
    } else {
      setDropZones([
        { id: 'upper', content: null, zoneType: 'default' },
        { id: 'lower', content: null, zoneType: 'default' }
      ]);
      setLabelZones([]); // No label zones in one-to-one mode
    }

    // Use the selected subset of pairs for the game
    let selectedItems: string[];
    if (gameMatchingMode === 'sort' || gameMatchingMode === 'category') {
      selectedItems = getGameItemsForMode(gameDataMapping, pairCount);
    } else {
      selectedItems = getRandomPairs(gameDataMapping, pairCount);
    }

    setGameItems(selectedItems);
    setCurrentGameData(gameDataMapping);
    setIsBuiltInGame(false);
    setGameQueue(selectedItems);
    setCompletedPairs(0);
    setMatchedItems(new Set());
    setSessionStats({ totalPairs: 0, totalAttempts: 0, correctMatches: 0 });

    // New algorithm: ≤5 pairs show all, ≥6 pairs use smart selection
    const initialItems = selectInitialItems(selectedItems, pairCount);

    initializeRectangles(initialItems);
    setGameStarted(true);
    setGameStartTime(Date.now());
    const modeLabel = gameMatchingMode === 'sort' ? ' 🏷️ Sort Mode' : gameMatchingMode === 'category' ? ' 📂 Category Mode' : '';
    setMessage(`🎮 ${message}${modeLabel}`);
    createParticles(window.innerWidth / 2, 200, 30);
    if (soundEnabled) {
      soundManager.current.playUploadSound();
    }
    if (hapticEnabled) {
      hapticManager.current.success();
    }

    // Clear uploaded dictionary after starting
    setUploadedDictionary(null);
  };

  const returnToGameSelection = () => {
    const wasAIGame = isAIGame;
    const wasSavedGame = isSavedGame;
    const wasAssignment = currentAssignmentId !== null;

    setGameStarted(false);
    setGameStartTime(null);
    setCurrentTime(0);
    setRectangles([]);
    setDropZones([
      { id: 'upper', content: null, zoneType: 'default' },
      { id: 'lower', content: null, zoneType: 'default' }
    ]);
    setCurrentMatchingMode('one-to-one'); // Reset to default mode
    setDraggedRectangle(null);
    setGameItems([]);
    setGameQueue([]);
    setCompletedPairs(0);
    setMatchedItems(new Set());
    setSessionStats({ totalPairs: 0, totalAttempts: 0, correctMatches: 0 });
    setCurrentGameData(null);
    setShowSinglePlayerResults(false);
    setSinglePlayerCompletionTime(null);
    setIsBuiltInGame(false);
    setIsAIGame(false);
    setIsSavedGame(false);
    setCurrentAssignmentId(null);
    setCurrentGameId(null);
    setRequiredPairCount(null);

    if (wasAssignment) {
      // Return to assignment view instead of game selection
      setDashboardInitialRole('student');
      setShowUserDashboard(true);
      setMessage('Return to your assignments');
    } else if (wasAIGame) {
      setShowAIDictionaryCreator(true);
      setMessage('Continue your AI chat or create a new game!');
    } else if (wasSavedGame) {
      setShowUserDashboard(true);
      setMessage('Your saved games');
    } else {
      // Return to home page (upload/built-in selection screen)
      setShowBuiltInGames(false);
      setShowGameTypeSelection(false);
      setSelectedGameType(null);
      setMessage('Select a game to play!');
    }
  };

  // Competitive Mode Functions
  const handleRoomJoined = (room: any, participants: any[]) => {
    console.log('🏠 Room joined callback received:', { room, participants });
    setCompetitiveRoom(room);
    setCompetitiveParticipants(participants);
    setIsCompetitiveHost(room.hostUserId === user?.id);

    // If joining a room that's between rounds (finished/topic_selection), show TopicSelection
    if (room.status === 'finished' || room.status === 'topic_selection') {
      setShowCompetitiveLobby(false);
      setCompetitiveResultsShown(true); // Base layer for TopicSelection
      setShowTopicSelection(true);
    }
  };

  const handleCompetitiveGameStart = (room: any, isHost: boolean) => {
    setCompetitiveRoom(room);
    setIsCompetitiveHost(isHost);
    setCompetitiveGameActive(true);
    setShowCompetitiveLobby(false);
    setCompetitiveResultsShown(false); // Reset results screen when game starts
    setShowTopicSelection(false); // Hide topic selection
    setShowPairCountSelection(false); // Hide pair count selection

    // Initialize competitive stats
    setCompetitiveStats({
      totalAttempts: 0,
      correctMatches: 0,
      wrongMatches: 0,
      startTime: Date.now()
    });

    // Start the game with the room's dictionary
    startCompetitiveGame(room);
  };

  const startCompetitiveGame = (room: any) => {
    console.log('🎮 Starting competitive game with room:', room);
    console.log('📖 Dictionary data:', room.dictionaryData);

    if (!room.dictionaryData) {
      console.error('❌ No dictionary data in room!');
      setMessage('Error: No game data available');
      return;
    }

    // Detect matching mode from room or dictionary data
    const gameMatchingMode = room.matching_mode ||
      (room.dictionaryData?.metadata?.matchingMode === 'category' ? 'category' : 'one-to-one');
    setCurrentMatchingMode(gameMatchingMode);
    console.log('🎯 Competitive game matching mode:', gameMatchingMode);

    // Use the room's game seed for consistent randomization
    const rng = seedrandom(room.gameSeed.toString());

    let pairs: string[] = [];

    if (gameMatchingMode === 'category') {
      // Category mode: use getGameItemsForMode
      pairs = getGameItemsForMode(room.dictionaryData, room.pairCount);
      // Apply seeded shuffle for consistent ordering across players
      for (let i = pairs.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
      }
    } else {
      // One-to-one mode: collect unique pairs
      const dictionaryKeys = Object.keys(room.dictionaryData);
      const allPairs: Array<[string, string]> = [];
      const seenPairs = new Set<string>();

      for (const key of dictionaryKeys) {
        const value = room.dictionaryData[key];
        const pairId = [key, value].sort().join('|');

        if (!seenPairs.has(pairId)) {
          seenPairs.add(pairId);
          allPairs.push([key, value]);
        }
      }

      // Shuffle all pairs using seeded random
      for (let i = allPairs.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [allPairs[i], allPairs[j]] = [allPairs[j], allPairs[i]];
      }

      // Take the first pairCount pairs from shuffled list
      const selectedPairs = allPairs.slice(0, room.pairCount);
      for (const [key, value] of selectedPairs) {
        pairs.push(key, value);
      }

      // Shuffle the final items again for display order
      for (let i = pairs.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
      }
    }

    // Set up game
    setCurrentGameData(room.dictionaryData);
    setGameQueue(pairs);
    setGameItems(pairs);
    setSelectedPairCount(room.pairCount);

    // Start game
    setGameStarted(true);
    const startTime = Date.now();
    setGameStartTime(startTime);
    setScore(0);
    setStreak(0);
    setCompletedPairs(0);
    setMatchedItems(new Set());

    // Set appropriate drop zones based on matching mode
    if (gameMatchingMode === 'category') {
      setDropZones([
        { id: 'item1', content: null, zoneType: 'item' },
        { id: 'item2', content: null, zoneType: 'item' },
        { id: 'category', content: null, zoneType: 'category' }
      ]);
    } else {
      setDropZones([
        { id: 'upper', content: null, zoneType: 'default' },
        { id: 'lower', content: null, zoneType: 'default' }
      ]);
    }

    // Initialize speed tracking for competitive mode
    setSpeedHistory([{ speed: gameSpeed, timestamp: startTime }]);

    // Initialize current user progress tracking
    if (user) {
      setCurrentUserProgress({
        userId: user.id,
        username: user.username,
        completed: 0,
        total: room.pairCount,
        attempts: 0,
        accuracy: 100,
        finished: false
      });
    }

    // Initialize all opponents from participants list
    if (room.participants && user) {
      const opponentsMap = new Map();
      room.participants.forEach((participant: any) => {
        if (participant.userId !== user.id) {
          opponentsMap.set(participant.userId, {
            userId: participant.userId,
            username: participant.username,
            completed: 0,
            total: room.pairCount,
            attempts: 0,
            accuracy: 100,
            finished: false
          });
        }
      });
      setCompetitiveOpponents(opponentsMap);
      console.log(`🎮 Initialized ${opponentsMap.size} opponents for competitive game`);
    }

    // Use smart initial selection: ≤5 pairs show all, ≥6 pairs use smart selection (max 5 pairs on screen)
    const initialItems = selectInitialItems(pairs, room.pairCount);

    initializeRectangles(initialItems);
  };

  const handleCompetitiveMatchAttempt = (correct: boolean) => {
    setCompetitiveStats(prev => ({
      ...prev,
      totalAttempts: prev.totalAttempts + 1,
      correctMatches: prev.correctMatches + (correct ? 1 : 0),
      wrongMatches: prev.wrongMatches + (correct ? 0 : 1)
    }));

    // Update current user's progress in real-time
    setCurrentUserProgress((prev: any) => {
      if (!prev) return prev;
      const newAttempts = prev.attempts + 1;
      const newCompleted = prev.completed + (correct ? 1 : 0);
      const newAccuracy = newAttempts > 0 ? (newCompleted / newAttempts) * 100 : 100;
      return {
        ...prev,
        attempts: newAttempts,
        completed: newCompleted,
        accuracy: newAccuracy
      };
    });

    // Notify other players of this match attempt in real-time
    if (competitiveRoom && user) {
      socketService.matchAttempt(competitiveRoom.id, user.id, correct);
    }
  };

  const handleCompetitiveGameComplete = () => {
    if (!competitiveRoom || !user) return;

    const completionTime = Date.now() - competitiveStats.startTime;

    // Calculate weighted average speed
    const endTime = Date.now();
    const history = [...speedHistory, { speed: gameSpeed, timestamp: endTime }]; // Add final speed

    let totalWeightedSpeed = 0;
    let totalTime = 0;

    for (let i = 0; i < history.length - 1; i++) {
      const duration = history[i + 1].timestamp - history[i].timestamp;
      totalWeightedSpeed += history[i].speed * duration;
      totalTime += duration;
    }

    const averageSpeed = totalTime > 0 ? totalWeightedSpeed / totalTime : gameSpeed;

    console.log('📊 Speed tracking:', {
      history,
      averageSpeed: averageSpeed.toFixed(2),
      totalTime: (totalTime / 1000).toFixed(1) + 's'
    });

    // Update current user's progress to finished
    setCurrentUserProgress((prev: any) => {
      if (!prev) return prev;
      return {
        ...prev,
        finished: true,
        completionTime: completionTime
      };
    });

    // Submit results with weighted average speed
    socketService.playerFinished(
      competitiveRoom.id,
      user.id,
      user.username,
      completionTime,
      competitiveStats.totalAttempts,
      competitiveStats.correctMatches,
      competitiveStats.wrongMatches,
      averageSpeed
    );

    // Stop the game for current user but keep leaderboard visible
    // Don't set competitiveResultsShown yet - wait for game_ended event
    setGameStarted(false);
  };

  const handleCompetitiveGameEnd = (results: any[]) => {
    setCompetitiveGameActive(false);
    setGameStarted(false);
  };

  const handleAnotherRound = async () => {
    console.log('🔄 handleAnotherRound called - current states:', {
      showCompetitiveLobby,
      competitiveResultsShown,
      showTopicSelection,
      competitiveGameActive,
      competitiveRoom: !!competitiveRoom,
      competitiveRoomCode: competitiveRoom?.roomCode
    });

    // Reset game state but keep room and players
    setGameStarted(false);
    setRectangles([]);
    setDropZones([]);
    setScore(0);
    setStreak(0);
    setCurrentTime(0);
    setCompletedPairs(0);
    setMatchedItems(new Set());

    // Reset competitive stats
    setCompetitiveStats({
      totalAttempts: 0,
      correctMatches: 0,
      wrongMatches: 0,
      startTime: 0
    });

    // Fetch current participants for topic selection
    if (competitiveRoom) {
      try {
        const roomDetails = await apiService.get(`/api/competitive/room/${competitiveRoom.roomCode}`);
        setCompetitiveParticipants(roomDetails.room.participants || []);
      } catch (error) {
        console.error('Error fetching participants:', error);
      }
    }

    // Show topic selection screen (keeping room and players)
    console.log('🎯 Setting states to show topic selection');
    setCompetitiveGameActive(false);
    setShowCompetitiveLobby(false);
    setCompetitiveResultsShown(true); // Always enable results screen for TopicSelection base layer
    setShowTopicSelection(true);
    console.log('✅ State updates queued');
  };

  const handleTopicApproved = (dictionary: { name: string; data: Record<string, string> }, newHostId: number) => {
    console.log('✅ Topic approved, new host:', newHostId);

    // Update room state with new dictionary and host
    if (competitiveRoom) {
      setCompetitiveRoom({
        ...competitiveRoom,
        dictionaryName: dictionary.name,
        dictionaryData: dictionary.data,
        hostUserId: newHostId
      });

      // Update if current user is now the host
      setIsCompetitiveHost(user?.id === newHostId);
    }

    // Show pair count selection screen (5-second countdown)
    setShowTopicSelection(false);
    setShowPairCountSelection(true);
  };

  const handlePairCountComplete = (selectedPairCount: number) => {
    console.log('🎯 Pair count selected:', selectedPairCount);

    // Update room with selected pair count
    if (competitiveRoom) {
      setCompetitiveRoom({
        ...competitiveRoom,
        pairCount: selectedPairCount
      });
    }

    // Now go to lobby with configured pairs
    setShowPairCountSelection(false);
    setShowCompetitiveLobby(true);
  };

  const handleBackFromTopicSelection = () => {
    // Just close topic selection overlay and return to results screen
    // Don't leave the room completely
    setShowTopicSelection(false);
    setCompetitiveResultsShown(true);
  };

  const handleLeaveCompetitive = () => {
    if (competitiveRoom && user) {
      socketService.leaveRoom(competitiveRoom.id, user.id);
    }

    // Reset game state
    setGameStarted(false);
    setRectangles([]);
    setDropZones([]);
    setScore(0);
    setStreak(0);
    setCurrentTime(0);

    // Reset competitive state
    setShowCompetitiveLobby(false);
    setShowTopicSelection(false);
    setCompetitiveRoom(null);
    setCompetitiveGameActive(false);
    setCompetitiveResultsShown(false);
    setGameMode('freeplay');
    setMessage('Choose a game mode to start playing');
  };

  // Note: getAvailableDictionaries removed - competitive mode now uses topicCategories directly

  const startBuiltInGame = (categoryId: string, pairCount: number) => {
    const category = builtInDictionaries.find(cat => cat.id === categoryId);
    if (!category) return;

    // Pre-select all pairs for the game and create queue
    const allGameItems = getRandomPairs(category.data, pairCount);

    // Reset game state
    setScore(0);
    setStreak(0);
    setCurrentTime(0);

    // Detect matching mode for this game
    const gameMatchingMode = detectMatchingMode(category.data);
    setCurrentMatchingMode(gameMatchingMode);

    // Set up drop zones based on matching mode
    if (gameMatchingMode === 'sort') {
      // Sort mode: Create dynamic label zones
      const labels = (category.data as any).labels || [];
      const zones = labels.map((label: string, index: number) => ({
        id: `label-${index}`,
        content: null,
        zoneType: 'label' as const,
        labelName: label
      }));
      setLabelZones(zones);
      setDropZones([]); // No traditional drop zones in sort mode
    } else if (gameMatchingMode === 'category') {
      setDropZones([
        { id: 'item1', content: null, zoneType: 'item' },
        { id: 'item2', content: null, zoneType: 'item' },
        { id: 'category', content: null, zoneType: 'category' }
      ]);
      setLabelZones([]); // No label zones in category mode
    } else {
      setDropZones([
        { id: 'upper', content: null, zoneType: 'default' },
        { id: 'lower', content: null, zoneType: 'default' }
      ]);
      setLabelZones([]); // No label zones in one-to-one mode
    }

    // For category and sort modes, use specialized item generation
    let gameItems: string[];
    if (gameMatchingMode === 'category' || gameMatchingMode === 'sort') {
      gameItems = getGameItemsForMode(category.data, pairCount);
    } else {
      gameItems = allGameItems;
    }

    setGameItems(gameItems);
    setCurrentGameData(category.data); // Store the full dictionary for matching
    setIsBuiltInGame(true); // Flag that this is a built-in game
    setGameQueue(gameItems);
    setCompletedPairs(0);
    setMatchedItems(new Set());
    setSessionStats({ totalPairs: 0, totalAttempts: 0, correctMatches: 0 });

    // Use the new algorithm for built-in games too
    const initialItems = selectInitialItems(gameItems, pairCount);

    initializeRectangles(initialItems);
    setGameStarted(true);
    setGameStartTime(Date.now());
    setShowBuiltInGames(false);

    const modeLabel = gameMatchingMode === 'category' ? '📂 Category Mode' : gameMatchingMode === 'sort' ? '🏷️ Sort Mode' : '';
    const countLabel = gameMatchingMode === 'sort' ? `${pairCount} items total` : `${pairCount} pairs total`;
    setMessage(`🎮 ${category.name} ${modeLabel} game started! ${countLabel}`);
    createParticles(window.innerWidth / 2, 200, 30);
    
    if (soundEnabled) {
      soundManager.current.playUploadSound();
    }
    if (hapticEnabled) {
      hapticManager.current.success();
    }
  };

  return (
    <div className={`App ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Background particles */}
      <div className="background-particles">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.x}px`,
              top: `${particle.y}px`,
              opacity: particle.life,
              transform: `scale(${particle.life})`,
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <header className="hero-header">
        <div className="brand-section">
          <div className="logo">
            <span className="logo-text">Universal Matching Game</span>
            <span className="logo-accent">Demo</span>
          </div>
        </div>

        {/* Game Mode Selector */}
        <div className="mode-selector">
          <button 
            className={`mode-btn ${gameMode === 'freeplay' ? 'active' : ''}`}
            onClick={() => setGameMode('freeplay')}
          >
            🎮 Freeplay Mode
          </button>
          
          {/* User Account Section */}
          {isAuthenticated ? (
            <button 
              className="mode-btn user-btn"
              onClick={async () => {
                // Check if user is suspended before showing dashboard
                try {
                  const response = await apiService.checkSuspensionStatus();
                  if (response.isSuspended) {
                    setSuspensionReason(response.suspensionData?.reason || 'Account suspended');
                    setShowSuspensionModal(true);
                  } else {
                    setShowUserDashboard(true);
                    setDashboardInitialRole(null); // No role = show account panel
                  }
                } catch (error) {
                  console.error('Failed to check suspension status:', error);
                  setShowUserDashboard(true);
                  setDashboardInitialRole(null);
                }
              }}
              title={`Account: ${user?.username}`}
              style={{ position: 'relative' }}
            >
              👤 {user?.username}
              {unreadNotificationCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    backgroundColor: '#ff4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '0.7rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid white',
                    fontWeight: 'bold'
                  }}
                >
                  {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                </span>
              )}
            </button>
          ) : (
            <button 
              className="mode-btn auth-btn"
              onClick={() => setShowAuth(true)}
            >
              🔐 Login / Register
            </button>
          )}
          
          {/* Dark/Light Mode Toggle */}
          <button 
            className="theme-toggle-btn"
            onClick={() => setDarkMode(!darkMode)}
            title={`Switch to ${darkMode ? 'Light' : 'Dark'} Mode`}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
          
          {/* Help Button */}
          <button 
            className="theme-toggle-btn"
            onClick={() => setShowHelpPopup(true)}
            title="How to Play"
          >
            ❓
          </button>
          <button
            className={`mode-btn ${gameMode === 'competition' ? 'active' : ''}`}
            onClick={() => {
              if (!user) {
                setMessage('⚠️ Please log in to play competitive mode');
                setShowAuth(true);
                setAuthMode('login');
                return;
              }
              setShowCompetitiveLobby(true);
              setGameMode('competition');
            }}
          >
            🏆 Competition Mode
          </button>
        </div>

        {/* Stats Grid - Different for each mode */}
        {gameMode === 'freeplay' ? (
          <div className="stats-grid freeplay">
            <div className="stat-card">
              <div className="stat-value">{score}</div>
              <div className="stat-label">Score</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{streak}</div>
              <div className="stat-label">Streak</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{sessionStats.totalPairs}</div>
              <div className="stat-label">Session Pairs</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {sessionStats.totalAttempts > 0 ? Math.round((sessionStats.correctMatches / sessionStats.totalAttempts) * 100) : 100}%
              </div>
              <div className="stat-label">Accuracy</div>
            </div>
          </div>
        ) : (
          <div className="stats-grid competition">
            <div className="stat-card">
              <div className="stat-value">{score}</div>
              <div className="stat-label">Score</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{highScore}</div>
              <div className="stat-label">High Score</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{streak}</div>
              <div className="stat-label">Streak</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {gameStarted && rectangles.length > 0 ? (currentTime / 1000).toFixed(1) : '0.0'}s
              </div>
              <div className="stat-label">Time</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {bestTime ? (bestTime / 1000).toFixed(1) : '--'}s
              </div>
              <div className="stat-label">Best Time</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{rectangles.length}</div>
              <div className="stat-label">Remaining</div>
            </div>
          </div>
        )}

        {/* Assignment Panel Buttons */}
        {!gameStarted && user && userRoles && (
          <div className="panel-access-section">
            {userRoles.isStudent && (
              <button
                className="panel-access-btn student-panel-btn"
                onClick={() => {
                  setDashboardInitialRole('student');
                  setShowUserDashboard(true);
                }}
              >
                📚 Student Dashboard
              </button>
            )}
            {userRoles.isTeacher && (
              <button
                className="panel-access-btn teacher-panel-btn"
                onClick={() => {
                  setDashboardInitialRole('teacher');
                  setShowUserDashboard(true);
                }}
              >
                👨‍🏫 Teacher Dashboard
              </button>
            )}
          </div>
        )}

        {!gameStarted && !showGameTypeSelection && !showBuiltInGames && !showMultipleChoiceSetup && !showMultipleChoiceGame && (
          <div className="upload-section">
          <input
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            id="dictionary-upload"
            disabled={isLoading}
          />
          <label htmlFor="dictionary-upload" className={`upload-btn ${isLoading ? 'loading' : ''}`}>
            {isLoading ? (
              <>
                <div className="loading-spinner"></div>
                Processing...
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" className="upload-icon">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Upload Game File
              </>
            )}
          </label>

          <div className="game-mode-divider">
            <span>OR</span>
          </div>

          <button
            className="upload-btn built-in-btn"
            onClick={() => {
              setShowGameTypeSelection(true);
              setSelectedGameType(null);
              setShowBuiltInGames(false);
            }}
          >
            🎯 Play Built-in Games
          </button>

          <div className="game-mode-divider">
            <span>OR</span>
          </div>

          <button
            className="upload-btn ai-creator-btn"
            onClick={() => setShowAIDictionaryCreator(!showAIDictionaryCreator)}
            disabled={!user}
          >
            <span className="ai-icon">🤖</span>
            Create Game with AI
            <span className="new-badge-inline">New!</span>
          </button>

          <div className="game-mode-divider">
            <span>OR</span>
          </div>
        </div>
        )}

        {/* Game Type Selection - First Level */}
        {showGameTypeSelection && !selectedGameType && !showMultipleChoiceSetup && (
          <GameTypeSelection
            onSelectType={(type) => {
              if (type === 'multiple-choice') {
                // Multiple Choice has its own setup flow
                setShowGameTypeSelection(false);
                setShowMultipleChoiceSetup(true);
              } else {
                setSelectedGameType(type);
                setSelectedTopic('');
                setSelectedCategory('');
                setShowGameTypeSelection(false);
                setShowBuiltInGames(true);
              }
            }}
            onBack={() => {
              setShowGameTypeSelection(false);
              setMessage('Choose a game mode to start playing');
            }}
          />
        )}

        {/* Multiple Choice Setup Flow */}
        {showMultipleChoiceSetup && !showMultipleChoiceGame && (
          <MultipleChoiceSetup
            onStartGame={(setup) => {
              setMultipleChoiceSetup(setup);
              setShowMultipleChoiceSetup(false);
              setShowMultipleChoiceGame(true);
            }}
            onBack={() => {
              setShowMultipleChoiceSetup(false);
              setShowGameTypeSelection(true);
            }}
          />
        )}

        {/* Multiple Choice Game */}
        {showMultipleChoiceGame && multipleChoiceSetup && (
          <MultipleChoiceGame
            setup={multipleChoiceSetup}
            onExit={() => {
              setShowMultipleChoiceGame(false);
              setMultipleChoiceSetup(null);
              setShowGameTypeSelection(true);
            }}
            onPlayAgain={() => {
              // Keep the same setup but restart
              setShowMultipleChoiceGame(false);
              setTimeout(() => {
                setShowMultipleChoiceGame(true);
              }, 100);
            }}
          />
        )}

        {/* Topic & Game Selection - Second Level */}
        {showBuiltInGames && selectedGameType && (
          <div className="built-in-games-panel">
            {!selectedTopic ? (
              // Topic Selection Screen (filtered by game type)
              <>
                <button
                  className="back-button-top-left"
                  onClick={() => {
                    setShowBuiltInGames(false);
                    setSelectedGameType(null);
                    setShowGameTypeSelection(true);
                  }}
                >
                  ← Back to Game Types
                </button>
                <h3 className="panel-title">
                  {selectedGameType === 'one-to-one' && '🔄 Pair Match - Choose Topic'}
                  {selectedGameType === 'category' && '📂 Pair & Classify - Choose Topic'}
                  {selectedGameType === 'sort' && '🏷️ Sort & Classify - Choose Topic'}
                </h3>
                <div className="category-selection">
                  <h4>Select a Topic:</h4>
                  <div className="category-grid">
                    {getTopicsByMode(selectedGameType).map(topic => (
                      <button
                        key={topic.id}
                        className="category-card"
                        onClick={() => setSelectedTopic(topic.id)}
                      >
                        <div className="category-icon">{topic.icon}</div>
                        <div className="category-name">{topic.name}</div>
                        <div className="category-description">{topic.description}</div>
                        <div className="game-count">{topic.games.length} games</div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              // Game Selection Screen
              <>
                <button
                  className="back-button-top-left"
                  onClick={() => {
                    setSelectedTopic('');
                    setSelectedCategory('');
                  }}
                >
                  ← Back to Topics
                </button>
                <h3 className="panel-title">
                  🎮 {getTopicsByMode(selectedGameType).find(t => t.id === selectedTopic)?.name} Games
                </h3>

                <div className="category-selection">
                  <h4>Select Game:</h4>
                  <div className="category-grid">
                    {getTopicsByMode(selectedGameType)
                      .find(topic => topic.id === selectedTopic)
                      ?.games.map(category => (
                        <button
                          key={category.id}
                          className={`category-card ${selectedCategory === category.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedCategory(category.id);
                            const maxPairsForCategory = getMaxPairsInDictionary(category.data);
                            setMaxPairs(maxPairsForCategory);
                            setSelectedPairCount(Math.min(selectedPairCount, maxPairsForCategory));
                          }}
                        >
                          <div className="category-icon">{category.icon}</div>
                          <div className="category-name">{category.name}</div>
                          <div className="category-description">{category.description}</div>
                        </button>
                      ))}
                  </div>
                </div>
                
                {selectedCategory && (
                  <>
                    <div className="pair-count-selection">
                      <h4>{selectedGameType === 'sort' ? 'Number of Items' : 'Number of Pairs'}: {selectedPairCount}</h4>
                      <div className="pair-count-slider">
                        <span>3</span>
                        <input
                          type="range"
                          min="3"
                          max={maxPairs}
                          value={selectedPairCount}
                          onChange={(e) => setSelectedPairCount(parseInt(e.target.value))}
                          className="slider"
                        />
                        <span>{maxPairs}</span>
                      </div>
                      <div className="slider-description">
                        Choose from 3 to {maxPairs} pairs available in this category
                      </div>
                    </div>
                    
                    <div className="game-start-section">
                      <button
                        className="start-game-btn"
                        onClick={() => startBuiltInGame(selectedCategory, selectedPairCount)}
                      >
                        🚀 Start Game
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {gameStarted && !competitiveResultsShown && (
          <div className="game-controls-section">
            <div className="return-control">
              <button
                className="return-to-games-btn large"
                onClick={returnToGameSelection}
                title="Return to game selection"
              >
                {currentAssignmentId ? '← Back to Assignments' : isAIGame ? '← Back to AI Chat' : isSavedGame ? '← Back to Profile' : '← Back to Games'}
              </button>
              <span>{currentAssignmentId ? 'Return to your assignments' : isAIGame ? 'Return to conversation or create new games' : isSavedGame ? 'Return to your saved dictionaries' : 'Choose a different game or topic'}</span>
            </div>
          </div>
        )}

        {uploadedDictionary && !gameStarted && (
          <div className="built-in-games-panel uploaded-dictionary-panel">
            <h3 className="panel-title">{requiredPairCount ? '📚 Assignment Game' : '📁 Configure Uploaded Dictionary'}</h3>

            <div className="dictionary-info">
              <p>✅ Dictionary loaded successfully!</p>
              <p>📊 Total {detectMatchingMode(uploadedDictionary.gameDataMapping) === 'sort' ? 'items' : 'pairs'} available: <strong>{getMaxPairsInDictionary(uploadedDictionary.gameDataMapping)}</strong></p>
              {requiredPairCount && (
                <p className="assignment-requirement">🎯 Required {detectMatchingMode(uploadedDictionary.gameDataMapping) === 'sort' ? 'items' : 'pairs'} to match: <strong>{requiredPairCount}</strong></p>
              )}
              <p>💬 {uploadedDictionary.message}</p>
            </div>

            {!requiredPairCount && (
              <>
                <div className="pair-count-selection">
                  <h4>{detectMatchingMode(uploadedDictionary.gameDataMapping) === 'sort' ? 'Number of Items' : 'Number of Pairs'}: {selectedPairCount}</h4>
                  <div className="pair-count-slider">
                    <span>3</span>
                    <input
                      type="range"
                      min="3"
                      max={maxPairs}
                      value={selectedPairCount}
                      onChange={(e) => setSelectedPairCount(parseInt(e.target.value))}
                      className="slider"
                    />
                    <span>{maxPairs}</span>
                  </div>
                  <div className="slider-description">
                    Choose from 3 to {maxPairs} {detectMatchingMode(uploadedDictionary.gameDataMapping) === 'sort' ? 'items' : 'pairs'} from your uploaded dictionary
                  </div>
                </div>

                <div className="game-start-section">
                  <button
                    className="start-game-btn"
                    onClick={() => startUploadedGame(selectedPairCount)}
                  >
                    🚀 Start Game
                  </button>

                  {isAuthenticated && (
                    <button
                      className="save-dictionary-btn"
                      onClick={() => {
                        setCurrentGameData(uploadedDictionary.gameDataMapping);
                        setShowSaveDictionary(true);
                      }}
                      title="Save this game to your account"
                    >
                      💾 Save Game
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {showAIDictionaryCreator && user && (
          <AIDictionaryCreator
            onDictionaryGenerated={handleAIDictionary}
            onClose={() => setShowAIDictionaryCreator(false)}
          />
        )}


        <div className="message-display">
          <div className="message">{message}</div>
        </div>

        {/* Progress Bar */}
        {gameStarted && !competitiveResultsShown && (() => {
          const totalPairsForProgress = selectedPairCount;
          return (
            <div className="progress-section">
              <div className="progress-info">
                <span>Progress: {completedPairs} / {totalPairsForProgress} pairs</span>
                <span>{Math.round((completedPairs / totalPairsForProgress) * 100)}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${(completedPairs / totalPairsForProgress) * 100}%` }}
                ></div>
              </div>
            </div>
          );
        })()}
      </header>

      {/* Speed Control - ABOVE game container, spans full width of game area + sidebar */}
      {gameStarted && !competitiveResultsShown && (
        <div className="game-container" style={{marginBottom: '0.5rem', padding: '1rem'}}>
          <div className="pair-count-selection" style={{width: '100%', maxWidth: 'none'}}>
            <h4>Game Speed: {gameSpeed}</h4>
            <div className="pair-count-slider">
              <span>1</span>
              <input
                type="range"
                min="1"
                max="10"
                value={gameSpeed}
                onChange={(e) => {
                  const newSpeed = parseInt(e.target.value);
                  setGameSpeed(newSpeed);
                  // Track speed change with timestamp for competitive mode
                  if (competitiveGameActive && gameStarted) {
                    setSpeedHistory(prev => [...prev, { speed: newSpeed, timestamp: Date.now() }]);
                  }
                }}
                className="slider"
              />
              <span>10</span>
            </div>
          </div>
        </div>
      )}

      {(gameStarted || (competitiveGameActive && !competitiveResultsShown)) && (
        <div
          className="game-container"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <div className="game-content-wrapper">
            {/* Competitive Mode: Show opponents progress section - visible during game and after user finishes */}
            {gameMode === 'competition' && competitiveGameActive && competitiveRoom && !competitiveResultsShown && (
              <div className="opponents-overlay">
                <div className="opponents-header">
                  <span className="room-code">Room: {competitiveRoom.roomCode}</span>
                  <span className="dictionary-topic">{competitiveRoom.dictionaryName}</span>
                  <div className="strategy-tip">⚡ Speed, accuracy & time affect your score!</div>
                </div>
                <div className="opponents-list">
                  {/* All players sorted by progress percentage (descending) */}
                  {(() => {
                    // Combine current user and opponents into single array
                    const allPlayers = [];

                    if (currentUserProgress) {
                      allPlayers.push({
                        ...currentUserProgress,
                        isCurrentUser: true,
                        progressPercentage: (currentUserProgress.completed / currentUserProgress.total) * 100
                      });
                    }

                    Array.from(competitiveOpponents.values()).forEach(opponent => {
                      allPlayers.push({
                        ...opponent,
                        isCurrentUser: false,
                        progressPercentage: (opponent.completed / opponent.total) * 100
                      });
                    });

                    // Sort by: finished first, then by progress percentage, then by accuracy
                    allPlayers.sort((a, b) => {
                      if (a.finished && !b.finished) return -1;
                      if (!a.finished && b.finished) return 1;
                      if (a.progressPercentage !== b.progressPercentage) {
                        return b.progressPercentage - a.progressPercentage;
                      }
                      return b.accuracy - a.accuracy;
                    });

                    // Render all players in sorted order
                    return allPlayers.map((player, index) => (
                      <div
                        key={player.userId}
                        className={`opponent-card ${player.isCurrentUser ? 'current-user-card' : ''}`}
                      >
                        <div className="opponent-info">
                          <span className={`opponent-name ${player.isCurrentUser ? 'current-user-name' : ''}`}>
                            <span className="rank-badge">#{index + 1}</span>
                            {player.isCurrentUser && '👤 '}
                            {player.username}
                            {player.isCurrentUser && <span className="you-badge">YOU</span>}
                          </span>
                          {player.finished && <span className="finished-badge">✓ Finished</span>}
                        </div>
                        <div className="opponent-progress">
                          <div className={`progress-bar ${player.isCurrentUser ? 'current-user-progress-bar' : ''}`}>
                            <div
                              className={`progress-fill ${player.isCurrentUser ? 'current-user-progress-fill' : ''}`}
                              style={{ width: `${player.progressPercentage}%` }}
                            />
                          </div>
                          <span className="progress-text">
                            {player.completed}/{player.total}
                          </span>
                        </div>
                        {player.attempts > 0 && !player.finished && (
                          <div className="opponent-accuracy">
                            Accuracy: {player.accuracy.toFixed(0)}%
                          </div>
                        )}
                        {player.finished && player.completionTime && (
                          <div className="opponent-time">
                            ⏱️ {(player.completionTime / 1000).toFixed(2)}s
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Game area - only show when game is actually running */}
            {gameStarted && (
              <div
                className="game-area"
                ref={gameAreaRef}
                style={{
                  width: `${currentGameAreaDimensions.width}px`,
                  height: `${currentGameAreaDimensions.height}px`
                }}
              >
            <div className="game-area-bg"></div>
            {showGameInstructions && (
              <div className="game-instructions-banner" onClick={() => setShowGameInstructions(false)}>
                <div className="game-instructions-text">
                  {currentMatchingMode === 'category' ? (
                    <>Drag <strong>2 matching items</strong> + their <strong>category</strong> into the 3 zones below</>
                  ) : (
                    <>Drag each item into the <strong>correct category folder</strong> on the right</>
                  )}
                </div>
                <span className="game-instructions-dismiss">tap to dismiss</span>
              </div>
            )}
            {rectangles.filter(rect =>
              !draggedRectangle || rect.content !== draggedRectangle.content
            ).map(rect => (
              <div
                key={rect.id}
                className={`moving-rectangle ${rect.collisionEffect ? 'collision' : ''}`}
                style={{
                  left: `${rect.x}px`,
                  top: `${rect.y}px`,
                  width: `${rect.width || currentRectangleDimensions.width}px`,
                  height: `${rect.height || currentRectangleDimensions.height}px`,
                  transform: `translate3d(0, 0, 0) rotate(${rect.rotation}deg) scale(${rect.scale})`,
                }}
                draggable={false}
                onPointerDown={(e) => {
                  // Use Pointer Events for better cross-platform compatibility
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Capture the pointer to ensure we get all events
                  const rectElement = e.currentTarget as HTMLElement;
                  rectElement.setPointerCapture(e.pointerId);
                  
                  const startX = e.clientX;
                  const startY = e.clientY;

                  // Find the rectangle by ID (not content, to handle duplicates like "in" = "in")
                  const targetRect = rectangles.find(r => r.id === rect.id);
                  if (!targetRect) return;

                  let isDragging = false;

                  const startDrag = () => {
                    if (isDragging) return;
                    isDragging = true;

                    // Set dragged rectangle state with dynamic dimensions
                    setDraggedRectangle({
                      id: rect.id,
                      content: rect.content,
                      x: startX,
                      y: startY,
                      rotation: targetRect.rotation,
                      scale: targetRect.scale,
                      width: rect.width,
                      height: rect.height,
                      fontSize: rect.fontSize,
                      lines: rect.lines,
                      isLongContent: rect.isLongContent
                    });

                    // Remove from rectangles array by ID (not content, to handle duplicate content)
                    setRectangles(prev => prev.filter(r => r.id !== rect.id));
                    
                    if (soundEnabled) {
                      soundManager.current.playDragSound();
                    }
                    if (hapticEnabled) {
                      hapticManager.current.lightTap();
                    }
                  };
                  
                  // Start drag immediately
                  startDrag();
                  
                  const handlePointerMove = (moveEvent: PointerEvent) => {
                    console.log('handlePointerMove called');
                    if (moveEvent.pointerId !== e.pointerId) return;
                    
                    moveEvent.preventDefault();
                    moveEvent.stopPropagation();
                    
                    if (!isDragging) {
                      startDrag();
                    }
                    
                    if (isDragging) {
                      const currentX = moveEvent.clientX;
                      const currentY = moveEvent.clientY;
                      
                      // Update dragged rectangle position
                      setDraggedRectangle(prev => prev ? {
                        ...prev,
                        x: currentX,
                        y: currentY,
                      } : prev);
                    }
                  };
                  
                  const handlePointerUp = (endEvent: PointerEvent) => {
                    console.log('handlePointerUp called!', endEvent.pointerId, e.pointerId);
                    if (endEvent.pointerId !== e.pointerId) return;
                    
                    console.log('handlePointerUp processing...');
                    endEvent.preventDefault();
                    endEvent.stopPropagation();
                    
                    // Remove listeners
                    document.removeEventListener('pointermove', handlePointerMove);
                    document.removeEventListener('pointerup', handlePointerUp);
                    document.removeEventListener('pointercancel', handlePointerUp);
                    
                    // Release pointer capture from the rectangle element
                    if (rectElement && rectElement.hasPointerCapture(e.pointerId)) {
                      rectElement.releasePointerCapture(e.pointerId);
                    }
                    
                    if (!isDragging) return;
                    
                    const finalX = endEvent.clientX;
                    const finalY = endEvent.clientY;
                    
                    // Check for drop zones when user releases (with generous margin)
                    // Use elementFromPoint to get the exact element under the pointer
                    const elementAtPoint = document.elementFromPoint(finalX, finalY);
                    let droppedOnZone: string | null = null;

                    console.log('=== POINTER RELEASE DEBUG ===');
                    console.log('Element at point:', elementAtPoint?.className, elementAtPoint?.tagName);
                    console.log('Current matching mode:', currentMatchingMode);
                    console.log('Label zones:', labelZones);

                    if (elementAtPoint) {
                      // Check if the element or any parent is a drop zone
                      const zoneElement = elementAtPoint.closest('.drop-zone, .label-zone');
                      console.log('Closest zone element:', zoneElement?.className, zoneElement);

                      if (zoneElement) {
                        // Detect zone type based on class
                        if (zoneElement.classList.contains('upper')) {
                          droppedOnZone = 'upper';
                        } else if (zoneElement.classList.contains('lower')) {
                          droppedOnZone = 'lower';
                        } else if (zoneElement.classList.contains('item-zone-1')) {
                          droppedOnZone = 'item1';
                        } else if (zoneElement.classList.contains('item-zone-2')) {
                          droppedOnZone = 'item2';
                        } else if (zoneElement.classList.contains('category-zone')) {
                          droppedOnZone = 'category';
                        } else if (zoneElement.classList.contains('label-zone')) {
                          // For label zones, get the id from data attribute
                          droppedOnZone = zoneElement.getAttribute('data-zone-id');
                          console.log('✓ Label zone detected with ID:', droppedOnZone);
                        }
                        console.log('✓ Drop detected on zone:', droppedOnZone, 'at coordinates:', finalX, finalY);
                      } else {
                        console.log('✗ No zone element found');
                      }
                    } else {
                      console.log('✗ No element at point');
                    }

                    console.log('Final droppedOnZone:', droppedOnZone);
                    console.log('=== END DEBUG ===');
                    
                    // Clear dragged rectangle
                    setDraggedRectangle(null);
                    
                    if (droppedOnZone) {
                      // Auto-drop into the zone
                      handleDropContent(rect.content, droppedOnZone);
                    } else {
                      // Slide back to game area - restore with ORIGINAL ID to avoid duplicates
                      const gameArea = getGameAreaDimensions(rectangles.length + 1);
                      const dynamicDims = getDynamicRectangleDimensions(rect.content, rectangles.length + 1);

                      const newRect: MovingRectangle = {
                        id: rect.id, // Use original ID to prevent duplicate content issues
                        content: rect.content,
                        x: Math.random() * Math.max(gameArea.width - dynamicDims.width, 0),
                        y: Math.random() * Math.max(gameArea.height - dynamicDims.height, 0),
                        vx: (Math.random() - 0.5) * 3.0,
                        vy: (Math.random() - 0.5) * 3.0,
                        rotation: targetRect.rotation,
                        scale: targetRect.scale,
                        collisionEffect: false,
                        width: dynamicDims.width,
                        height: dynamicDims.height,
                        fontSize: dynamicDims.fontSize,
                        lines: dynamicDims.lines || [rect.content]
                      };
                      
                      setRectangles(prev => [...prev, newRect]);
                    }
                  };
                  
                  // Add listeners immediately
                  document.addEventListener('pointermove', handlePointerMove, { passive: false });
                  document.addEventListener('pointerup', handlePointerUp, { passive: false });
                  document.addEventListener('pointercancel', handlePointerUp, { passive: false });
                }}
                onMouseEnter={() => {
                  if (soundEnabled) {
                    soundManager.current.playHoverSound();
                  }
                }}
              >
                <div 
                  className={`rectangle-content ${getEmojiClass(rect.content)}`}
                  style={{
                    fontSize: isEmoji(rect.content) ? 
                      ((rect.width || currentRectangleDimensions.width) < 80 ? '1.4rem' : '1.8rem') : 
                      (rect.fontSize || currentRectangleDimensions.fontSize),
                    lineHeight: isEmoji(rect.content) ? '1' : '1.4',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: rect.isLongContent ? '8px 50px' : '8px 20px', // Adaptive padding based on content length
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  {rect.lines && rect.lines.length > 0 ? (
                    rect.lines.map((line, index) => (
                      <div key={index} style={{ 
                        margin: '0', 
                        padding: '0', 
                        lineHeight: '1.4',
                        fontSize: 'inherit',
                        whiteSpace: 'nowrap'
                      }}>
                        {line}
                      </div>
                    ))
                  ) : (
                    rect.content
                  )}
                </div>
                <div className="rectangle-glow"></div>
              </div>
            ))}
          </div>
            )}
          </div>

          {gameStarted && <div className="sidebar">
            {/* Refresh Button */}
            <button
              className="refresh-rectangles-btn"
              onClick={handleRefreshRectangles}
              title="Shuffle current rectangles"
              disabled={rectangles.length === 0}
            >
              🔄 Refresh
            </button>

            <div className="drop-zone-container">
              {currentMatchingMode === 'sort' ? (
                /* Sort Mode: Dynamic label boxes */
                <div className={`label-zones-container ${isVerticalLayout ? 'vertical' : 'horizontal'}`}>
                  {labelZones.map((zone) => (
                    <div
                      key={zone.id}
                      className="label-zone"
                      data-zone-id={zone.id}
                      onDrop={(e) => handleDrop(e, zone.id)}
                      onDragOver={handleDragOver}
                    >
                      <div className="label-zone-inner">
                        <div className="label-name">{zone.labelName}</div>
                        <div className="label-icon">📂</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : currentMatchingMode === 'category' ? (
                /* Category Mode: 3 zones - 2 items + 1 category */
                <>
                  <div className="item-zones-row">
                    <div
                      className={`drop-zone item-zone item-zone-1 ${dropZones.find(z => z.id === 'item1')?.content ? 'filled' : ''}`}
                      onDrop={(e) => handleDrop(e, 'item1')}
                      onDragOver={handleDragOver}
                    >
                      <div className="drop-zone-inner">
                        <div className="drop-zone-content">
                          {dropZones.find(z => z.id === 'item1')?.content ? (
                            <div
                              className={getEmojiClass(dropZones.find(z => z.id === 'item1')!.content!)}
                              style={{
                                fontSize: isEmoji(dropZones.find(z => z.id === 'item1')!.content!) ? '2rem' : '1rem',
                                lineHeight: '1'
                              }}
                            >
                              {dropZones.find(z => z.id === 'item1')!.content}
                            </div>
                          ) : (
                            <>
                              <div className="drop-icon">📌</div>
                              <div className="drop-text">Item 1</div>
                            </>
                          )}
                        </div>
                        <div className="drop-zone-glow"></div>
                      </div>
                    </div>

                    <div
                      className={`drop-zone item-zone item-zone-2 ${dropZones.find(z => z.id === 'item2')?.content ? 'filled' : ''}`}
                      onDrop={(e) => handleDrop(e, 'item2')}
                      onDragOver={handleDragOver}
                    >
                      <div className="drop-zone-inner">
                        <div className="drop-zone-content">
                          {dropZones.find(z => z.id === 'item2')?.content ? (
                            <div
                              className={getEmojiClass(dropZones.find(z => z.id === 'item2')!.content!)}
                              style={{
                                fontSize: isEmoji(dropZones.find(z => z.id === 'item2')!.content!) ? '2rem' : '1rem',
                                lineHeight: '1'
                              }}
                            >
                              {dropZones.find(z => z.id === 'item2')!.content}
                            </div>
                          ) : (
                            <>
                              <div className="drop-icon">📌</div>
                              <div className="drop-text">Item 2</div>
                            </>
                          )}
                        </div>
                        <div className="drop-zone-glow"></div>
                      </div>
                    </div>
                  </div>

                  <div className="match-indicator">⬇ MATCH TO ⬇</div>

                  <div
                    className={`drop-zone category-zone ${dropZones.find(z => z.id === 'category')?.content ? 'filled' : ''}`}
                    onDrop={(e) => handleDrop(e, 'category')}
                    onDragOver={handleDragOver}
                  >
                    <div className="drop-zone-inner">
                      <div className="drop-zone-content">
                        {dropZones.find(z => z.id === 'category')?.content ? (
                          <div
                            className={getEmojiClass(dropZones.find(z => z.id === 'category')!.content!)}
                            style={{
                              fontSize: isEmoji(dropZones.find(z => z.id === 'category')!.content!) ? '2.5rem' : '1.2rem',
                              lineHeight: '1'
                            }}
                          >
                            {dropZones.find(z => z.id === 'category')!.content}
                          </div>
                        ) : (
                          <>
                            <div className="drop-icon">🏷️</div>
                            <div className="drop-text">Category</div>
                          </>
                        )}
                      </div>
                      <div className="drop-zone-glow"></div>
                    </div>
                  </div>
                </>
              ) : (
                /* One-to-One Mode: 2 zones (existing layout) */
                <>
                  <div
                    className={`drop-zone upper ${dropZones[0].content ? 'filled' : ''}`}
                    onDrop={(e) => handleDrop(e, 'upper')}
                    onDragOver={handleDragOver}
                  >
                    <div className="drop-zone-inner">
                      <div className="drop-zone-content">
                        {dropZones[0].content ? (
                          <div
                            className={getEmojiClass(dropZones[0].content)}
                            style={{
                              fontSize: isEmoji(dropZones[0].content) ? '2.5rem' : '1.2rem',
                              lineHeight: '1'
                            }}
                          >
                            {dropZones[0].content}
                          </div>
                        ) : (
                          <>
                            <div className="drop-icon">⬆</div>
                            <div className="drop-text">First Item</div>
                          </>
                        )}
                      </div>
                      <div className="drop-zone-glow"></div>
                    </div>
                  </div>

                  <div className="vs-indicator">VS</div>

                  <div
                    className={`drop-zone lower ${dropZones[1].content ? 'filled' : ''}`}
                    onDrop={(e) => handleDrop(e, 'lower')}
                    onDragOver={handleDragOver}
                  >
                    <div className="drop-zone-inner">
                      <div className="drop-zone-content">
                        {dropZones[1].content ? (
                          <div
                            className={getEmojiClass(dropZones[1].content)}
                            style={{
                              fontSize: isEmoji(dropZones[1].content) ? '2.5rem' : '1.2rem',
                              lineHeight: '1'
                            }}
                          >
                            {dropZones[1].content}
                          </div>
                        ) : (
                          <>
                            <div className="drop-icon">⬇</div>
                            <div className="drop-text">Second Item</div>
                          </>
                        )}
                      </div>
                      <div className="drop-zone-glow"></div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>}

          {/* Dragged Rectangle Overlay - shown during drag */}
          {gameStarted && draggedRectangle && (
            <div
              className="dragged-rectangle"
              style={{
                position: 'fixed',
                left: draggedRectangle.x - (draggedRectangle.width || currentRectangleDimensions.width) / 2,
                top: draggedRectangle.y - (draggedRectangle.height || currentRectangleDimensions.height) / 2,
                width: `${draggedRectangle.width || currentRectangleDimensions.width}px`,
                height: `${draggedRectangle.height || currentRectangleDimensions.height}px`,
                transform: `translate3d(0, 0, 0) rotate(${draggedRectangle.rotation}deg) scale(${draggedRectangle.scale})`,
                zIndex: 1000,
                pointerEvents: 'none',
              }}
            >
              <div 
                className={`rectangle-content ${getEmojiClass(draggedRectangle.content)}`}
                style={{
                  fontSize: isEmoji(draggedRectangle.content) ? '1.8rem' : (draggedRectangle.fontSize || currentRectangleDimensions.fontSize),
                  lineHeight: isEmoji(draggedRectangle.content) ? '1' : '1.4',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: draggedRectangle.isLongContent ? '8px 50px' : '8px 20px', // Adaptive padding
                  overflow: 'hidden',
                  boxSizing: 'border-box'
                }}
              >
                {draggedRectangle.lines && draggedRectangle.lines.length > 0 ? (
                  draggedRectangle.lines.map((line, index) => (
                    <div key={index} style={{ 
                      margin: '0', 
                      padding: '0', 
                      lineHeight: '1.4',
                      fontSize: 'inherit',
                      whiteSpace: 'nowrap'
                    }}>
                      {line}
                    </div>
                  ))
                ) : (
                  draggedRectangle.content
                )}
              </div>
              <div className="rectangle-glow"></div>
            </div>
          )}
        </div>
      )}


      {/* Success Popup */}
      {successPopup && (
        <div className="success-popup">
          <div className="success-popup-content">
            {successPopup}
          </div>
        </div>
      )}

      {/* Mismatch Popup */}
      {mismatchPopup && (
        <div className="mismatch-popup">
          <div className="mismatch-popup-content">
            {mismatchPopup}
          </div>
        </div>
      )}

      {/* Competition Mode Popup */}
      {showCompetitionPopup && (
        <div className="competition-popup-overlay" onClick={() => setShowCompetitionPopup(false)}>
          <div className="competition-popup" onClick={(e) => e.stopPropagation()}>
            <div className="competition-popup-content">
              <h3>🏆 Competition Mode</h3>
              <p>This feature is currently under development and will be released soon!</p>
              <p>Stay tuned for exciting competitive features including:</p>
              <ul>
                <li>Timed challenges</li>
                <li>Global leaderboards</li>
                <li>Daily tournaments</li>
                <li>Achievement system</li>
              </ul>
              <button 
                className="popup-close-btn"
                onClick={() => setShowCompetitionPopup(false)}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Popup */}
      {showHelpPopup && (
        <div className="competition-popup-overlay" onClick={() => setShowHelpPopup(false)}>
          <div className="help-popup" onClick={(e) => e.stopPropagation()}>
            <button
              className="help-close-btn"
              onClick={() => setShowHelpPopup(false)}
              aria-label="Close help"
            >
              ✕
            </button>
            <div className="help-popup-content">
              <h2>❓ How to Play</h2>

              <section className="help-section">
                <h3>🎯 Game Objective</h3>
                <p>Match pairs by dragging rectangles into drop zones. Complete all matches to win!</p>
              </section>

              <section className="help-section">
                <h3>🎮 Basic Gameplay</h3>
                <ol>
                  <li>Drag moving rectangles from the game area into the <strong>upper</strong> and <strong>lower</strong> drop zones on the right</li>
                  <li>When both zones are filled, the game checks if they're a matching pair</li>
                  <li><strong>Correct match:</strong> +2 points, items disappear, new ones spawn</li>
                  <li><strong>Wrong match:</strong> -1 point, items bounce back to the game area</li>
                  <li>Continue until all matches are complete!</li>
                </ol>
              </section>

              <section className="help-section">
                <h3>📁 Game Modes</h3>
                <div className="help-subsection">
                  <h4>🏠 Free Play (Solo Mode)</h4>
                  <ul>
                    <li><strong>Built-in Games:</strong> Click "← Games" to browse pre-made categories (languages, science, etc.)</li>
                    <li><strong>Upload Custom:</strong> Click "Upload Game File" to use your own JSON file with matching pairs</li>
                    <li><strong>AI Game Creator:</strong> Click "Create Game with AI" to generate custom games by describing what you want</li>
                    <li><strong>Saved Games:</strong> Access your personally created and saved games from your dashboard</li>
                  </ul>
                </div>

                <div className="help-subsection">
                  <h4>🤖 AI Game Creator</h4>
                  <ul>
                    <li>Describe the game you want (e.g., "Spanish verbs and translations" or "Capital cities of Europe")</li>
                    <li>Optionally provide a website URL for the AI to analyze and create matches from</li>
                    <li>The AI generates matching pairs based on your description</li>
                    <li>Test the game immediately or save it to your account for later</li>
                    <li>You can have multiple AI chat sessions to create different games</li>
                  </ul>
                </div>

                <div className="help-subsection">
                  <h4>⚔️ Competition Mode (Multiplayer)</h4>
                  <ul>
                    <li><strong>Create Room:</strong> Choose a game topic, set number of matches, and get a room code</li>
                    <li><strong>Join Room:</strong> Enter a room code to join an existing game</li>
                    <li><strong>Topic Voting:</strong> Before each round, players vote on the topic (host breaks ties)</li>
                    <li><strong>Live Competition:</strong> All players see the same matches in the same order</li>
                    <li><strong>Scoring:</strong> Compete for the highest score - speed, accuracy, and time all matter!</li>
                    <li><strong>Multiple Rounds:</strong> Play as many rounds as you want with the same players</li>
                    <li><strong>Real-time Updates:</strong> See opponents' progress and scores as you play</li>
                  </ul>
                </div>
              </section>

              <section className="help-section">
                <h3>⚙️ Features & Controls</h3>
                <ul>
                  <li><strong>Timer:</strong> Track your completion time and beat your records</li>
                  <li><strong>Progress Bar:</strong> Visual indicator of how many matches you've completed</li>
                  <li><strong>Score Display:</strong> See your current score and watch it update in real-time</li>
                  <li><strong>Dark/Light Mode:</strong> Toggle theme with the button in the top right</li>
                  <li><strong>Sound Effects:</strong> Enable/disable audio feedback for matches</li>
                  <li><strong>Pause:</strong> Take a break anytime in free play mode</li>
                </ul>
              </section>

              <section className="help-section">
                <h3>💡 Pro Tips</h3>
                <ul>
                  <li>Move quickly but think carefully - wrong matches cost you points!</li>
                  <li>Watch the rectangles move around to plan your next drag</li>
                  <li>In competition mode, speed matters - but accuracy matters more!</li>
                  <li>Save your custom games to build a personal library</li>
                  <li>Share games with friends using the share feature</li>
                </ul>
              </section>

              <section className="help-section">
                <h3>📊 Scoring System</h3>

                <div className="help-subsection">
                  <h4>🏠 Free Play Mode</h4>
                  <ul>
                    <li><strong>Correct Match:</strong> +2 points</li>
                    <li><strong>Wrong Match:</strong> -1 point</li>
                    <li>Simple scoring - focus on accuracy and completion time</li>
                  </ul>
                </div>

                <div className="help-subsection">
                  <h4>⚔️ Competition Mode</h4>
                  <ul>
                    <li><strong>Base Points:</strong> +2 for correct, -1 for wrong (same as Free Play)</li>
                    <li><strong>Speed Multiplier:</strong> Faster matches earn bonus points</li>
                    <li><strong>Accuracy Bonus:</strong> High accuracy percentage increases your score</li>
                    <li><strong>Time Bonus:</strong> Completing the game quickly adds extra points</li>
                    <li><strong>Complex Formula:</strong> Final score = (base points × speed multiplier) + accuracy bonus + time bonus</li>
                    <li>Strategy matters - balance speed with accuracy for maximum score!</li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Modal */}
      {showAuth && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              {authMode === 'login' ? (
                <LoginForm
                  onClose={() => setShowAuth(false)}
                  onSuccess={() => {
                    // Show success popup, then close auth modal after 2 seconds
                    setShowLoginSuccess(true);
                    setTimeout(() => {
                      setShowLoginSuccess(false);
                      setShowAuth(false);
                    }, 2000);
                  }}
                  onFailure={(message) => {
                    // Show failure popup, then return to login form after 2 seconds
                    setLoginFailureMessage(message);
                    setShowLoginFailure(true);
                    setTimeout(() => {
                      setShowLoginFailure(false);
                    }, 2000);
                  }}
                  onSwitchToRegister={() => setAuthMode('register')}
                  onForgotPassword={() => {
                    setShowAuth(false);
                    setShowForgotPassword(true);
                  }}
                />
              ) : (
                <RegisterForm
                  onClose={() => setShowAuth(false)}
                  onSuccess={(response) => {
                    if (response.requiresVerification && response.userId && response.email) {
                      // New flow: show email verification
                      setPendingUser({ userId: response.userId, email: response.email });
                      setShowEmailVerification(true);
                      setShowAuth(false);
                    } else {
                      // Old flow: show success popup, then switch to login
                      setShowRegisterSuccess(true);
                      setTimeout(() => {
                        setShowRegisterSuccess(false);
                        setAuthMode('login');
                      }, 2000);
                    }
                  }}
                  onFailure={(message) => {
                    // Show failure popup, then return to register form after 2 seconds
                    setRegisterFailureMessage(message);
                    setShowRegisterFailure(true);
                    setTimeout(() => {
                      setShowRegisterFailure(false);
                    }, 2000);
                  }}
                  onSwitchToLogin={() => setAuthMode('login')}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Dashboard Modal */}
      {showUserDashboard && (
        <div className="popup-overlay">
          <div className="popup-container large">
            <div className="popup-content">
              <UserDashboard
                initialRole={dashboardInitialRole}
                onSelectDictionary={(dictionary) => {
                  // Set up dictionary as uploaded game
                  const allGameItems = Object.keys(dictionary.data);
                  setCurrentGameData(dictionary.data);
                  setUploadedDictionary({
                    items: allGameItems,
                    gameDataMapping: dictionary.data,
                    message: `Playing "${dictionary.name}"`
                  });

                  // Set max pairs for saved dictionary using proper pair counting
                  const maxPairsForSaved = getMaxPairsInDictionary(dictionary.data);
                  setMaxPairs(maxPairsForSaved);
                  setSelectedPairCount(Math.min(selectedPairCount, maxPairsForSaved));

                  setIsBuiltInGame(false);
                  setIsAIGame(false);
                  setIsSavedGame(true);
                  setShowUserDashboard(false);
                  setMessage(`Playing "${dictionary.name}" - Choose number of matches and start!`);
                }}
                onStartGame={(dictionary, assignmentId, gameId, pairCount) => {
                  setCurrentAssignmentId(assignmentId || null);
                  setCurrentGameId(gameId || null);
                  setShowUserDashboard(false);
                  setDashboardInitialRole(null);
                  handleAssignmentGame(dictionary, pairCount);
                }}
                onBackToGame={() => {
                  setShowUserDashboard(false);
                  setDashboardInitialRole(null);
                  refreshNotificationCount(); // Refresh count when returning to game
                }}
                onLogout={() => {
                  setShowUserDashboard(false);
                  setDashboardInitialRole(null);
                  setShowLogoutSuccess(true);
                  setTimeout(() => {
                    setShowLogoutSuccess(false);
                  }, 2000);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Save Dictionary Modal */}
      {showSaveDictionary && isAuthenticated && currentGameData && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <h3>Save Your Game</h3>
              <p>Save this game topic to your account for future play!</p>
              <div className="form-group">
                <label htmlFor="dictionaryName">Game Name:</label>
                <input
                  type="text"
                  id="dictionaryName"
                  value={saveDictionaryName}
                  onChange={(e) => setSaveDictionaryName(e.target.value)}
                  placeholder="Enter a name for your game"
                  maxLength={50}
                />
              </div>
              <div className="popup-actions">
                <button 
                  onClick={() => setShowSaveDictionary(false)}
                  className="secondary-btn"
                >
                  Cancel
                </button>
                <button 
                  onClick={async () => {
                    if (saveDictionaryName.trim() && currentGameData) {
                      try {
                        await apiService.saveDictionary(saveDictionaryName.trim(), currentGameData);
                        setMessage(`Game "${saveDictionaryName}" saved successfully!`);
                        setShowSaveDictionary(false);
                        setSaveDictionaryName('');
                      } catch (error) {
                        setMessage(`Failed to save game: ${error instanceof Error ? error.message : 'Unknown error'}`);
                      }
                    }
                  }}
                  className="primary-btn"
                  disabled={!saveDictionaryName.trim()}
                >
                  Save Dictionary
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout Success Modal */}
      {showLogoutSuccess && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <div className="auth-form">
                <h2>Logout Successful!</h2>
                <div className="success-message">
                  You have been logged out successfully.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Success Popup */}
      {showLoginSuccess && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <div className="auth-form">
                <div style={{fontSize: '3rem', marginBottom: '1rem', color: '#4CAF50'}}>✓</div>
                <h2 style={{color: '#4CAF50'}}>Login Successful!</h2>
                <div className="success-message">
                  Welcome back! Redirecting...
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register Success Popup */}
      {showRegisterSuccess && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <div className="auth-form">
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>🎉</div>
                <h2 style={{color: '#4CAF50'}}>Registration Successful!</h2>
                <div className="success-message">
                  Welcome! Please login with your new account.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Failure Popup */}
      {showLoginFailure && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <div className="auth-form">
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>❌</div>
                <h2 style={{color: '#f44336'}}>Login Failed</h2>
                <div className="error-message">
                  {loginFailureMessage}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register Failure Popup */}
      {showRegisterFailure && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <div className="auth-form">
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>❌</div>
                <h2 style={{color: '#f44336'}}>Registration Failed</h2>
                <div className="error-message">
                  {registerFailureMessage}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Verification Modal */}
      {showEmailVerification && pendingUser && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <EmailVerificationForm
                userId={pendingUser.userId}
                email={pendingUser.email}
                onSuccess={() => {
                  setShowEmailVerification(false);
                  setPendingUser(null);
                  setShowRegisterSuccess(true);
                  setTimeout(() => {
                    setShowRegisterSuccess(false);
                    setShowAuth(true);
                    setAuthMode('login');
                  }, 2000);
                }}
                onBack={() => {
                  setShowEmailVerification(false);
                  setPendingUser(null);
                  setShowAuth(true);
                  setAuthMode('register');
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-content">
              <ForgotPasswordForm
                onSuccess={() => {
                  setShowForgotPassword(false);
                  setShowRegisterSuccess(true);
                  setTimeout(() => {
                    setShowRegisterSuccess(false);
                    setShowAuth(true);
                    setAuthMode('login');
                  }, 2000);
                }}
                onBack={() => {
                  setShowForgotPassword(false);
                  setShowAuth(true);
                  setAuthMode('login');
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Competitive Lobby */}
      {showCompetitiveLobby && !competitiveGameActive && (
        <div className="popup-overlay">
          <div className="popup-container large">
            <CompetitiveLobby
              onGameStart={handleCompetitiveGameStart}
              onBack={handleLeaveCompetitive}
              onProceedToTopics={handleAnotherRound}
              onRoomJoined={handleRoomJoined}
              topicCategories={topicCategories}
              existingRoom={competitiveRoom}
            />
          </div>
        </div>
      )}

      {/* Topic Selection Overlay (for another round) */}
      {showTopicSelection && competitiveRoom && (
        <div className="popup-overlay">
          <div className="popup-container large">
            <TopicSelection
              room={competitiveRoom}
              participants={competitiveParticipants}
              topicCategories={topicCategories}
              onTopicApproved={handleTopicApproved}
              onBack={handleBackFromTopicSelection}
              onParticipantsUpdate={setCompetitiveParticipants}
            />
          </div>
        </div>
      )}

      {/* Pair Count Selection Overlay (after topic approval) */}
      {showPairCountSelection && competitiveRoom && (
        <PairCountSelection
          room={competitiveRoom}
          isHost={isCompetitiveHost}
          maxPairs={getMaxPairsInDictionary(competitiveRoom.dictionaryData)}
          onComplete={handlePairCountComplete}
        />
      )}

      {/* Single Player Results Screen */}
      {showSinglePlayerResults && singlePlayerCompletionTime && (
        <div className="single-player-results">
          <div className="results-card">
            <h2>Game Complete!</h2>
            <div className="results-stats">
              <div className="result-item">
                <span className="result-label">Time</span>
                <span className="result-value">{(singlePlayerCompletionTime / 1000).toFixed(1)}s</span>
              </div>
              <div className="result-item">
                <span className="result-label">Score</span>
                <span className="result-value">{score}</span>
              </div>
              <div className="result-item">
                <span className="result-label">Accuracy</span>
                <span className="result-value">
                  {sessionStats.totalAttempts > 0
                    ? `${Math.round((sessionStats.correctMatches / sessionStats.totalAttempts) * 100)}%`
                    : '100%'}
                </span>
              </div>
              <div className="result-item">
                <span className="result-label">Matches</span>
                <span className="result-value">{sessionStats.correctMatches} / {selectedPairCount}</span>
              </div>
              {bestTime && (
                <div className="result-item">
                  <span className="result-label">Best Time</span>
                  <span className="result-value">{(bestTime / 1000).toFixed(1)}s</span>
                </div>
              )}
            </div>
            <div className="results-actions">
              <button className="result-btn primary" onClick={() => {
                setShowSinglePlayerResults(false);
                setSinglePlayerCompletionTime(null);
                returnToGameSelection();
              }}>
                Back to Games
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Competitive Game Component - mounted during gameplay to listen for game_ended, shows results when received */}
      {competitiveGameActive && competitiveRoom && (
        <CompetitiveGame
          room={competitiveRoom}
          isHost={isCompetitiveHost}
          onGameEnd={handleCompetitiveGameEnd}
          onLeave={handleLeaveCompetitive}
          onAnotherRound={handleAnotherRound}
          renderGame={(gameData, pairCount, seed, onMatchAttempt, onGameComplete) => null}
        />
      )}

      {/* Suspended User Modal */}
      <SuspendedUserModal
        isOpen={showSuspensionModal}
        onClose={() => setShowSuspensionModal(false)}
        suspensionReason={suspensionReason}
      />

      {/* Copyright Footer */}
      <div className="copyright-footer">
        © 2025 Universal Matching Game
        <span className="info-icon" title="UniversalMatching.com is a Dartmouth Neukom CompX project (CompX 2025 grant to profs. Mikhail Gronas and Sergey Bratus). Developed by Lin Shi '25 and Ruichen Zheng '26. Concept by Mikhail Gronas and Mark Sobolev. Testing by Michael-Aquila Gao.">
          ℹ️
        </span>
        <div className="credit-tooltip">
          <strong>UniversalMatching.com</strong> is a Dartmouth Neukom CompX project (CompX 2025 grant to profs. Mikhail Gronas and Sergey Bratus).
          <br/><br/>
          <strong>Developed by:</strong> Lin Shi '25 and Ruichen Zheng '26
          <br/>
          <strong>Concept by:</strong> Mikhail Gronas and Mark Sobolev
          <br/>
          <strong>Testing by:</strong> Michael-Aquila Gao
        </div>
      </div>
    </div>
  );
};

export default App;
